/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — clean-sheet rebuild (People Rebuild §3, binding).
   Top orchestrator: wires the split hooks/components together
   (context, prefs, undo, multiplayer, drag/snap, nodes/edges,
   toolbar, palette, menus). Intentionally still one component at the
   composition layer — every cross-cutting concern (snap, undo,
   multiplayer, drag) now lives in its own hook file and reaches here
   via explicit props/context, never a window global.

   EDIT-SESSION MODEL (People Rebuild §0/§3, Josh grant 2026-08-17):
   the chart opens in VIEW MODE for everyone. "Edit chart" (edit
   permission only) opens an edit SESSION — every mutation from that
   point on (drag, nudge, align/distribute, edge create/reconnect/
   delete, style/color/label, waypoints, junction spawn/delete,
   palette add-to-chart, the reports-to mirror) is LOCAL React state
   only: it lands directly in React Flow `nodes`/`edges` state plus a
   small diff-bookkeeping hook (useEditSession), never a network call.
   Save builds ONE `commitOrgChart` transaction from that diff; the
   caller's `refreshAll()` (PeoplePage's loadAll) then re-syncs
   `members`/`orgChartEdges`/`orgChartJunctions` from the server, which
   is what closes the "positions never refresh" defect for good — the
   sender always sees its own write. The ONLY immediate write left is
   `onRemoveFromChart` (its own destructive confirm; also deletes
   team_assignments, which isn't part of the commit payload), and it's
   only reachable while a session is open.
   ═══════════════════════════════════════════════════════════════ */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  Position,
  type Node,
  type Edge,
  type NodeMouseHandler,
  type Connection,
  type IsValidConnection,
  type OnNodeDrag,
  ConnectionMode,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/base.css';
import './orgchart.css';
import EmptyState from '../../../components/EmptyState';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { Button } from '../../../components/ui/button';
import type { TeamMember, Team } from '../../../../shared/types';
import type {
  OrgChartEdge,
  OrgChartJunction,
  OrgChartNote,
  OrgChartView,
} from '../../../../shared/entities';

import type {
  NoteNodeData,
  OrgChartV2Props,
  OrgEdgeData,
  PersonNodeData,
  PositionUpdate,
} from './types';
import { NODE_WIDTH, NODE_HEIGHT, parseEndpoint } from './types';
import { getLayoutedElements } from './dagreLayout';
import {
  getEdgePolyline,
  computeEdgeHops,
  geometrySignature,
  projectPointOntoPolyline,
  projectPointOntoPolylineIndexed,
  buildEdgePoints,
  endpointFlex,
  type EdgeGeometry,
  type ObstacleRect,
  type Pt,
} from './edgeGeometry';
import { computeTreeRoutes, type TreeEdgeInput } from './edgeTreeRoute';
import { commitOrgChart } from './commitOrgChart';
import { OrgChartViewService } from '../../../services/orgChartViewService';
import { nodeTypes } from './nodes';
import { edgeTypes } from './edges/OrgEdgeV2';
import { OrgChartCanvasProvider, type SnapTargets } from './context/OrgChartCanvasContext';
import { useOrgChartPrefs } from './hooks/useOrgChartPrefs';
import { useOrgChartViewSelection } from './hooks/useOrgChartViewSelection';
import { useUndo, type NotePatchFields } from './hooks/useUndo';
import { useEditSession, edgeFromRF, noteFromRF } from './hooks/useEditSession';
import { useMultiplayer } from './hooks/useMultiplayer';
import { useNodeDragAndSnap, collectSubtreeTeamIds } from './hooks/useNodeDragAndSnap';
import { PaletteBar } from './PaletteBar';
import { OrgChartToolbar } from './Toolbar';
import { LegendOverlay } from './LegendOverlay';
import { RemoteCursors, SnapGuideLines } from './overlays';
import { EdgeMenu } from './menus/EdgeMenu';
import { NodeContextMenu } from './menus/NodeContextMenu';
import { CanvasContextMenu } from './menus/CanvasContextMenu';
import { TextBoxMenu } from './menus/TextBoxMenu';
import { JunctionMenu } from './menus/JunctionMenu';
import { SavedViewsBar } from './views/SavedViewsBar';
import { rem } from '../../../lib/rem';

function autoPickHandles(
  parent: TeamMember,
  child: TeamMember
): { source: 'top' | 'bottom' | 'left' | 'right'; target: 'top' | 'bottom' | 'left' | 'right' } {
  const pX = (parent.orgX ?? 0) + NODE_WIDTH / 2;
  const pY = (parent.orgY ?? 0) + NODE_HEIGHT / 2;
  const cX = (child.orgX ?? 0) + NODE_WIDTH / 2;
  const cY = (child.orgY ?? 0) + NODE_HEIGHT / 2;
  const dx = cX - pX;
  const dy = cY - pY;
  if (Math.abs(dy) >= Math.abs(dx)) {
    return dy >= 0 ? { source: 'bottom', target: 'top' } : { source: 'top', target: 'bottom' };
  }
  return dx >= 0 ? { source: 'right', target: 'left' } : { source: 'left', target: 'right' };
}

/** Reference-stability check for a computed point list — lets the
 *  trunk-and-bus router (cheap, recomputed every render) still hand
 *  back the SAME array identity when a family's geometry didn't
 *  change, matching the `dd.autoWaypoints === nextAuto` no-op guard
 *  the live-injection effect already relies on. */
/** Shared reference for "this auto edge has no mid-points" — reused so
 *  the live-injection effect's `dd.autoWaypoints === nextAuto` no-op
 *  guard holds across renders for every plain 2-point auto edge
 *  (People Rebuild 2026-08-18: nothing left to compute for these once
 *  A* is retired — see `autoRoutesByEdgeId`). */
const EMPTY_MID: Pt[] = [];

function samePts(a: Pt[], b: Pt[] | undefined): boolean {
  if (!b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].x !== b[i].x || a[i].y !== b[i].y) return false;
  }
  return true;
}

/** Public entry point — mirrors the old `OrgChart` export shape
 *  (ReactFlowProvider wrapper around the real workspace). */
export function OrgChartV2(props: OrgChartV2Props) {
  return (
    <ReactFlowProvider>
      <OrgChartV2Inner {...props} />
    </ReactFlowProvider>
  );
}

function OrgChartV2Inner({
  members,
  teams,
  teamAssignments,
  departments,
  billingRoles,
  roleName,
  onMemberClick,
  onTeamAssign,
  onTeamRemove,
  onToggleTeamRole,
  onRemoveFromChart,
  onDeleteMember,
  orgChartEdges,
  orgChartJunctions,
  orgChartNotes = [],
  canEdit,
  canViewBilling,
  canViewRoles,
  refreshAll,
  toast,
  orgChartViews,
  orgChartViewPositions = [],
  currentTeamMemberId,
  isViewAdmin,
  roles,
  onDirtyChange,
}: OrgChartV2Props) {
  const [search, setSearch] = useState('');
  const [ctxMenu, setCtxMenu] = useState<{ memberId: string; x: number; y: number } | null>(null);
  const [canvasMenu, setCanvasMenu] = useState<{ x: number; y: number } | null>(null);
  const [edgeMenu, setEdgeMenu] = useState<{ edgeId: string; x: number; y: number } | null>(null);
  const [noteMenu, setNoteMenu] = useState<{ noteId: string; x: number; y: number } | null>(null);
  /** Junction right-click menu (People Rebuild, Josh law 2026-08-17:
   *  right-clicking a junction used to delete it instantly — now opens
   *  a real menu, "Delete junction" only for now). */
  const [junctionMenu, setJunctionMenu] = useState<{
    junctionId: string;
    x: number;
    y: number;
  } | null>(null);
  /** Which text box (raw note id, no `note-` prefix) is currently
   *  inline-editing, if any. Injected into the matching node's `data`
   *  by a live effect below (same pattern as the edge hover/hop
   *  injection) so it can change mid-session without tripping the
   *  session-guarded props resync. */
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [labelEditor, setLabelEditor] = useState<{
    edgeId: string;
    value: string;
    x: number;
    y: number;
  } | null>(null);
  const [chartConfirm, setChartConfirm] = useState<
    | { kind: 'autoArrange' }
    | { kind: 'removeFromChart'; ids: string[]; names: string }
    | { kind: 'cancelSession' }
    | null
  >(null);

  const prefs = useOrgChartPrefs(teams);
  const {
    snapEnabled,
    setSnapEnabled,
    snapEnabledRef,
    visibleTeamIds,
    setTeamBoxVisible,
    setAllTeamsVisible,
    nodeStyle,
    setNodeStyle,
    legendVisible,
    setLegendVisible,
  } = prefs;
  const viewSelection = useOrgChartViewSelection(prefs, orgChartViews);
  /** Main = the implicit chart (no view row) — positions on
   *  team_members.orgX/orgY, reportsTo is real hierarchy. A custom
   *  board (activeViewId set) is a separate canvas: its own placements
   *  (org_chart_view_positions), lines/junctions/notes (viewId-stamped
   *  rows), and reportsTo stays untouched — see the Main-only guards
   *  below (migration 041, Josh grant 2026-08-17). */
  const isMain = viewSelection.activeViewId == null;

  // ─── Edit session (People Rebuild §0/§3) ───
  const session = useEditSession();
  /** Editing affordances are actually LIVE right now. `canEdit` alone
   *  is just the permission — View mode renders exactly like
   *  `editable=false` used to (drag/connect/menus all off), whether or
   *  not the user COULD open a session. */
  const editable = canEdit && session.active;

  // No-work-loss hardening (2026-08-19): tell the shell whenever this
  // session goes dirty/clean so it can block a tab switch (or any other
  // action that would unmount this component) instead of silently
  // discarding an in-progress edit — the root cause traced for Josh's
  // "lost a bunch of work" reports that AREN'T explained by a failed
  // commit (a failed commit already correctly keeps the session open,
  // see handleSaveSession's catch below).
  useEffect(() => {
    onDirtyChange?.(session.active && session.dirtyCount > 0);
    // Defensive: if this component unmounts for any other reason while
    // still reporting dirty, clear the shell's flag on the way out so it
    // never stays stuck blocking future switches.
    return () => onDirtyChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.active, session.dirtyCount]);

  const snapTargetsRef = useRef<SnapTargets>({ xs: [], ys: [] });

  function getMemberTeams(mid: string): Team[] {
    return teamAssignments
      .filter((a) => a.teamMemberId === mid)
      .map((a) => teams.find((t) => t.id === a.teamId))
      .filter(Boolean) as Team[];
  }
  function getMemberTeamColor(mid: string): string {
    return getMemberTeams(mid)[0]?.color || 'var(--ac)';
  }

  // ─── Board-scoped slices of the multi-canvas tables (migration 041):
  // Main's rows carry viewId null/undefined, a custom board's rows carry
  // that board's view id — filtered here ONCE so every downstream
  // consumer (initial derivation, computeChartKey, snap targets) agrees
  // on "this board's data" without re-filtering. ───
  const boardEdges = useMemo(
    () => orgChartEdges.filter((e) => (e.viewId ?? null) === viewSelection.activeViewId),
    [orgChartEdges, viewSelection.activeViewId]
  );
  const boardJunctions = useMemo(
    () => orgChartJunctions.filter((j) => (j.viewId ?? null) === viewSelection.activeViewId),
    [orgChartJunctions, viewSelection.activeViewId]
  );
  const boardNotes = useMemo(
    () => orgChartNotes.filter((n) => (n.viewId ?? null) === viewSelection.activeViewId),
    [orgChartNotes, viewSelection.activeViewId]
  );
  const boardViewPositions = useMemo(
    () =>
      isMain ? [] : orgChartViewPositions.filter((p) => p.viewId === viewSelection.activeViewId),
    [orgChartViewPositions, viewSelection.activeViewId, isMain]
  );
  const viewPositionByMemberId = useMemo(
    () => new Map(boardViewPositions.map((p) => [p.memberId, p])),
    [boardViewPositions]
  );
  /** Saved (pre-drag) position for a member on the CURRENTLY ACTIVE
   *  board — see UseNodeDragAndSnapDeps.resolvePosition for why this
   *  replaced a direct `members.find(...).orgX/orgY` read. */
  const resolveMemberPosition = useCallback(
    (id: string): { x: number; y: number } | undefined => {
      if (isMain) {
        const m = members.find((mm) => mm.id === id);
        return m && m.orgX != null && m.orgY != null ? { x: m.orgX, y: m.orgY } : undefined;
      }
      const p = viewPositionByMemberId.get(id);
      return p ? { x: p.x, y: p.y } : undefined;
    },
    [isMain, members, viewPositionByMemberId]
  );

  // ─── On-chart membership from PROPS (server truth) — used ONLY to
  // (re)derive the initial/resync node set. Once a session is active,
  // `nodes`/`edges` React Flow state IS the truth; see `chartMemberIds`
  // below for the render-time on-chart set used by the palette.
  // Main derives membership from edges/reportsTo/team assignment
  // (unchanged). A custom board's membership is exactly "has a row in
  // org_chart_view_positions for this board" — nothing else (People
  // Rebuild §0, "views become separate canvases/boards"). ───
  const onChart = new Set<string>();
  if (isMain) {
    for (const e of boardEdges) {
      onChart.add(e.sourceMemberId);
      onChart.add(e.targetMemberId);
    }
    for (const m of members) {
      if (m.reportsTo === '__root__' || teamAssignments.some((a) => a.teamMemberId === m.id)) {
        onChart.add(m.id);
      }
    }
  } else {
    for (const p of boardViewPositions) onChart.add(p.memberId);
  }
  const chartMembers = members.filter((m) => onChart.has(m.id) && m.active);

  // ─── Shared node builders — used by the initial/resync derivation
  // AND by every in-session mutation that adds a node (palette drop,
  // junction spawn), so both paths produce identical node shapes. ───
  const buildPersonNode = useCallback(
    (m: TeamMember, position: { x: number; y: number }): Node<PersonNodeData> => {
      const mTeams = getMemberTeams(m.id);
      const isLeader = teamAssignments.some(
        (a) => a.teamMemberId === m.id && a.teamRole === 'leader'
      );
      const titleRow = m.billingRoleId
        ? billingRoles.find((b) => b.id === m.billingRoleId)
        : undefined;
      const pills: PersonNodeData['pills'] = [];
      if (m.departmentId) {
        const dept = departments.find((d) => d.id === m.departmentId);
        if (dept)
          pills.push({
            kind: 'department',
            label: dept.abbreviation || dept.name,
            color: dept.color || 'var(--ac)',
            fullLabel: dept.name,
          });
      }
      for (const t of mTeams)
        pills.push({ kind: 'team', label: t.name, color: t.color || getMemberTeamColor(m.id) });
      const teamMemberships = mTeams.map((t) => ({
        team: t,
        isLeader: teamAssignments.some(
          (a) => a.teamMemberId === m.id && a.teamId === t.id && a.teamRole === 'leader'
        ),
      }));
      return {
        id: m.id,
        type: 'person' as const,
        data: {
          member: m,
          teamColor: getMemberTeamColor(m.id),
          pills,
          isLeader,
          companyBadge: titleRow?.abbreviation
            ? {
                code: titleRow.abbreviation,
                color: titleRow.color || 'var(--ac)',
                name: titleRow.name,
              }
            : undefined,
          nodeStyle,
          canViewBilling,
          canViewRoles,
          roleLabel: roleName(m),
          teamMemberships,
        },
        position,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      teamAssignments,
      teams,
      billingRoles,
      departments,
      nodeStyle,
      canViewBilling,
      canViewRoles,
      roleName,
    ]
  );

  const buildJunctionNode = useCallback(
    (j: { id: string; x: number; y: number; label?: string; color?: string }): Node => ({
      id: `junction-${j.id}`,
      type: 'junction',
      position: { x: j.x, y: j.y },
      data: { label: j.label, color: j.color },
      // No per-node draggable/connectable: those OVERRIDE the canvas-level
      // nodesDraggable/nodesConnectable props and would freeze at their
      // build-time value (the session guard blocks mid-session rebuilds,
      // so a view-mode-built node would stay locked all session).
      selectable: true,
      zIndex: 5,
    }),
    []
  );

  const buildNoteNode = useCallback(
    (n: OrgChartNote): Node<NoteNodeData> => ({
      id: `note-${n.id}`,
      type: 'textBox',
      position: { x: n.x, y: n.y },
      data: { text: n.text, fontSize: n.fontSize, color: n.color, width: n.width },
      // No per-node draggable/connectable — see buildJunctionNode's trap
      // note; canvas-level nodesDraggable governs.
      selectable: true,
      zIndex: 6,
    }),
    []
  );

  // ─── Build initial nodes/edges from PROPS (server truth) ───
  const { initialNodes, initialEdges } = useMemo(() => {
    const memberById = new Map(chartMembers.map((m) => [m.id, m]));
    const teamById = new Map(teams.map((t) => [t.id, t]));
    const junctionById = new Map(boardJunctions.map((j) => [j.id, j]));
    const endpointExists = (id: string, kind?: 'member' | 'team' | 'junction') => {
      if (kind === 'team') return teamById.has(id);
      if (kind === 'junction') return junctionById.has(id);
      return memberById.has(id);
    };
    const endpointNodeId = (id: string, kind?: 'member' | 'team' | 'junction') => {
      if (kind === 'team') return `team-box-${id}`;
      if (kind === 'junction') return `junction-${id}`;
      return id;
    };
    const rfEdges: Edge[] = boardEdges
      .filter(
        (e) =>
          endpointExists(e.sourceMemberId, e.sourceType) &&
          endpointExists(e.targetMemberId, e.targetType)
      )
      .map((e) => {
        const sourceId = endpointNodeId(e.sourceMemberId, e.sourceType);
        const targetId = endpointNodeId(e.targetMemberId, e.targetType);
        let sourceHandle = e.sourceHandle;
        let targetHandle = e.targetHandle;
        if (!sourceHandle || !targetHandle) {
          if ((e.sourceType || 'member') === 'member' && (e.targetType || 'member') === 'member') {
            const parent = memberById.get(e.sourceMemberId)!;
            const child = memberById.get(e.targetMemberId)!;
            const auto = autoPickHandles(parent, child);
            sourceHandle = sourceHandle || auto.source;
            targetHandle = targetHandle || auto.target;
          }
        }
        return {
          id: e.id,
          source: sourceId,
          target: targetId,
          sourceHandle,
          targetHandle,
          type: 'org',
          data: {
            edgeId: e.id,
            waypoints: e.waypoints || [],
            lineStyle: e.style || 'solid',
            color: e.color,
            label: e.label,
            sourceMemberId: e.sourceMemberId,
            targetMemberId: e.targetMemberId,
            canEdit: editable,
          } as OrgEdgeData,
          style: { stroke: e.color || 'var(--tx)', strokeWidth: e.thickness || 1.5 },
        } as Edge;
      });

    // Defense against legacy ghost rows (found 2026-08-18): edges with an
    // empty-string endpoint id can never mount in React Flow AND would sit
    // in edge state where a session commit could re-persist them. Filter
    // them out of existence at the boundary. (Server now rejects them too.)
    const validRfEdges = rfEdges.filter((e) => e.source && e.target);

    // Position source swap (migration 041, "positions source swapped"):
    // Main reads team_members.orgX/orgY; a custom board reads its own
    // org_chart_view_positions row. A brand-new view has no rows here
    // yet, so its first render is a genuinely blank canvas.
    const memberPosition = (m: TeamMember): { x: number; y: number } => {
      if (isMain) {
        return m.orgX != null && m.orgY != null ? { x: m.orgX, y: m.orgY } : { x: 0, y: 0 };
      }
      const p = viewPositionByMemberId.get(m.id);
      return p ? { x: p.x, y: p.y } : { x: 0, y: 0 };
    };
    let rfNodes: Node<PersonNodeData>[] = chartMembers.map((m) =>
      buildPersonNode(m, memberPosition(m))
    );

    const unsavedIds = new Set(
      chartMembers
        .filter((m) =>
          isMain ? m.orgX == null || m.orgY == null : !viewPositionByMemberId.has(m.id)
        )
        .map((m) => m.id)
    );
    if (unsavedIds.size > 0) {
      // Members with no saved position yet get a one-off dagre layout so
      // they don't stack at (0,0) — RENDER-ONLY. There is no more
      // fire-and-forget network save here (edit-session model: the ONLY
      // write path is a commit inside an open session) — the position
      // becomes real once someone drags them (in a session) and saves.
      const layouted = getLayoutedElements(rfNodes, validRfEdges);
      rfNodes = rfNodes.map((n) =>
        unsavedIds.has(n.id)
          ? { ...n, position: layouted.find((ln) => ln.id === n.id)?.position ?? n.position }
          : n
      );
    }

    const junctionNodes: Node[] = boardJunctions.map(buildJunctionNode);
    const noteNodes: Node[] = boardNotes.map(buildNoteNode);

    return {
      initialNodes: [...rfNodes, ...junctionNodes, ...noteNodes],
      initialEdges: validRfEdges,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chartMembers.map((m) => m.id).join(','),
    boardEdges
      .map(
        (e) =>
          `${e.id}:${e.sourceMemberId}:${e.targetMemberId}:${e.sourceType || ''}:${e.targetType || ''}:${e.style || ''}:${e.color || ''}:${e.label || ''}:${e.sourceHandle || ''}:${e.targetHandle || ''}:${(e.waypoints || []).length}`
      )
      .join('|'),
    boardJunctions.map((j) => `${j.id}:${j.x}:${j.y}:${j.label || ''}:${j.color || ''}`).join('|'),
    boardNotes
      .map((n) => `${n.id}:${n.x}:${n.y}:${n.text}:${n.fontSize}:${n.color || ''}:${n.width || ''}`)
      .join('|'),
    nodeStyle,
    editable,
    isMain,
    viewPositionByMemberId,
    buildPersonNode,
    buildJunctionNode,
    buildNoteNode,
  ]);

  /** The signature the guarded resync effect (and the session-external-
   *  change notice) compare against — deliberately excludes position
   *  (live dragging must never retrigger a resync; on a custom board
   *  that also means view-position x/y churn, same exclusion Main
   *  already had for orgX/orgY — board MEMBERSHIP, not coordinates,
   *  drives resync, and membership is already fully captured by
   *  `chartMembers`' id list below). */
  const computeChartKey = useCallback(
    () =>
      (viewSelection.activeViewId || 'main') +
      '|m:' +
      chartMembers
        .map((m) => m.id)
        .sort()
        .join('|') +
      '|e:' +
      boardEdges
        .map(
          (e) =>
            `${e.id}:${e.sourceMemberId}:${e.targetMemberId}:${e.sourceType || ''}:${e.targetType || ''}:${e.style || ''}:${e.color || ''}:${e.label || ''}:${e.sourceHandle || ''}:${e.targetHandle || ''}`
        )
        .sort()
        .join(',') +
      '|j:' +
      boardJunctions
        .map((j) => `${j.id}:${j.x}:${j.y}:${j.label || ''}:${j.color || ''}`)
        .sort()
        .join(',') +
      '|nt:' +
      boardNotes
        .map(
          (n) => `${n.id}:${n.x}:${n.y}:${n.text}:${n.fontSize}:${n.color || ''}:${n.width || ''}`
        )
        .sort()
        .join(',') +
      '|ta:' +
      teamAssignments
        .map((a) => `${a.teamMemberId}-${a.teamId}`)
        .sort()
        .join(',') +
      '|ns:' +
      JSON.stringify(nodeStyle),
    [
      viewSelection.activeViewId,
      chartMembers,
      boardEdges,
      boardJunctions,
      boardNotes,
      teamAssignments,
      nodeStyle,
    ]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const { remoteCursors, localDraggingIds, broadcastDrag, onCanvasMouseMove, onCanvasMouseLeave } =
    useMultiplayer(setNodes);

  /** Outbound multiplayer stays silent while a session is open — other
   *  viewers see the result on Save via the commit's broadcast, not a
   *  live drag stream (People Rebuild edit-session grant). Cursor
   *  sharing (onCanvasMouseMove/Leave) is untouched — both directions
   *  stay live regardless of session state. */
  const guardedBroadcastDrag = useCallback(
    (
      payload: Array<{ id: string; x: number; y: number; type: 'person' | 'junction' }>,
      active: boolean
    ) => {
      if (session.active) return;
      broadcastDrag(payload, active);
    },
    [broadcastDrag, session.active]
  );

  const effReportsTo = useCallback(
    (m: TeamMember): string => {
      const override = session.reportsToOverrides.get(m.id);
      return override !== undefined ? override : m.reportsTo || '';
    },
    [session.reportsToOverrides]
  );

  // ─── Session-local mutation primitives ──────────────────────────
  // Same function SHAPES as the removed network props — they drop into
  // useNodeDragAndSnap/useUndo's existing `deps` contract unchanged.
  // Every one of these both (a) mutates React Flow nodes/edges state
  // directly so the change renders immediately, and (b) notes itself
  // in the session's diff bookkeeping so Save's commit picks it up.

  const applyPositionsLocal = useCallback(
    (updates: PositionUpdate[]) => {
      setNodes((prev) =>
        prev.map((n) => {
          const u = updates.find((x) => x.id === n.id);
          return u ? { ...n, position: { x: u.orgX, y: u.orgY } } : n;
        })
      );
      session.markPositionsTouched(updates.map((u) => u.id));
    },
    [setNodes, session]
  );

  const applyJunctionPositionLocal = useCallback(
    (id: string, patch: { x: number; y: number }) => {
      const nodeId = `junction-${id}`;
      setNodes((prev) =>
        prev.map((n) => (n.id === nodeId ? { ...n, position: { x: patch.x, y: patch.y } } : n))
      );
      session.noteJunctionUpdated(id);
    },
    [setNodes, session]
  );

  /** Position-only writer for text-box notes, used by drag-stop —
   *  mirrors applyJunctionPositionLocal. */
  const applyNotePositionLocal = useCallback(
    (id: string, patch: { x: number; y: number }) => {
      const nodeId = `note-${id}`;
      setNodes((prev) =>
        prev.map((n) => (n.id === nodeId ? { ...n, position: { x: patch.x, y: patch.y } } : n))
      );
      session.noteNoteUpdated(id);
    },
    [setNodes, session]
  );

  const sessionCreateNote = useCallback(
    async (note: OrgChartNote) => {
      setNodes((prev) => [...prev, buildNoteNode(note)]);
      session.noteNoteCreated(note.id);
    },
    [setNodes, session, buildNoteNode]
  );

  const sessionUpdateNote = useCallback(
    async (id: string, patch: Partial<OrgChartNote>) => {
      const nodeId = `note-${id}`;
      setNodes((prev) =>
        prev.map((n) => {
          if (n.id !== nodeId) return n;
          const dd = (n.data || {}) as NoteNodeData;
          const nextData: NoteNodeData = { ...dd };
          if (patch.text !== undefined) nextData.text = patch.text;
          if (patch.fontSize !== undefined) nextData.fontSize = patch.fontSize;
          if ('color' in patch) nextData.color = patch.color;
          if (patch.width !== undefined) nextData.width = patch.width;
          const nextPosition =
            patch.x !== undefined || patch.y !== undefined
              ? { x: patch.x ?? n.position.x, y: patch.y ?? n.position.y }
              : n.position;
          return { ...n, position: nextPosition, data: nextData };
        })
      );
      session.noteNoteUpdated(id);
    },
    [setNodes, session]
  );

  const sessionDeleteNote = useCallback(
    async (id: string) => {
      setNodes((prev) => prev.filter((n) => n.id !== `note-${id}`));
      session.noteNoteDeleted(id);
    },
    [setNodes, session]
  );

  /** Remove a node (member OR junction, by its RF composite id) and
   *  cascade-remove every edge that referenced it — returns the full
   *  `OrgChartEdge` shape of everything removed, for undo/removal
   *  snapshots. Shared by junction-delete and the remove-from-chart
   *  reconciliation. */
  const removeNodeAndCascadeEdges = useCallback(
    (nodeId: string): OrgChartEdge[] => {
      const referencing = edges.filter(
        (e) => e.type === 'org' && (e.source === nodeId || e.target === nodeId)
      );
      setEdges((prev) =>
        prev.filter((e) => !(e.type === 'org' && (e.source === nodeId || e.target === nodeId)))
      );
      setNodes((prev) => prev.filter((n) => n.id !== nodeId));
      const removed: OrgChartEdge[] = [];
      for (const e of referencing) {
        const eid = ((e.data || {}) as OrgEdgeData).edgeId || e.id;
        session.noteEdgeDeleted(eid);
        const full = edgeFromRF(e, eid);
        if (full) removed.push(full);
      }
      return removed;
    },
    [edges, setEdges, setNodes, session]
  );

  const sessionCreateEdge = useCallback(
    async (patch: Partial<OrgChartEdge>) => {
      if (!patch.sourceMemberId || !patch.targetMemberId) return;
      const id = patch.id || crypto.randomUUID();
      const sourceType = patch.sourceType || 'member';
      const targetType = patch.targetType || 'member';
      const sourceNodeId =
        sourceType === 'team'
          ? `team-box-${patch.sourceMemberId}`
          : sourceType === 'junction'
            ? `junction-${patch.sourceMemberId}`
            : patch.sourceMemberId;
      const targetNodeId =
        targetType === 'team'
          ? `team-box-${patch.targetMemberId}`
          : targetType === 'junction'
            ? `junction-${patch.targetMemberId}`
            : patch.targetMemberId;
      const newEdge: Edge = {
        id,
        source: sourceNodeId,
        target: targetNodeId,
        sourceHandle: patch.sourceHandle,
        targetHandle: patch.targetHandle,
        type: 'org',
        data: {
          edgeId: id,
          waypoints: patch.waypoints || [],
          lineStyle: patch.style || 'solid',
          color: patch.color,
          label: patch.label,
          sourceMemberId: patch.sourceMemberId,
          targetMemberId: patch.targetMemberId,
          canEdit: editable,
        } as OrgEdgeData,
        style: { stroke: patch.color || 'var(--tx)', strokeWidth: patch.thickness || 1.5 },
      };
      setEdges((prev) => [...prev, newEdge]);
      session.noteEdgeCreated(id);

      // Reports-to MIRROR (moved in from the old network onCreateEdge
      // handler — a member-to-member edge auto-sets the target's
      // manager if they didn't already have one). Main-only (migration
      // 041, Josh grant 2026-08-17): reports_to is org-wide truth driven
      // ONLY by Main — a custom board's lines are pure visual
      // arrangement, never hierarchy, so this mirror is a no-op there.
      if (isMain && sourceType === 'member' && targetType === 'member') {
        const target = members.find((m) => m.id === patch.targetMemberId);
        const current = target ? effReportsTo(target) : '';
        if (!current || current === '__root__') {
          session.setReportsTo(patch.targetMemberId, patch.sourceMemberId);
        }
      }
    },
    [setEdges, session, members, editable, effReportsTo, isMain]
  );

  const sessionUpdateEdge = useCallback(
    async (id: string, patch: Partial<OrgChartEdge>) => {
      setEdges((prev) =>
        prev.map((e) => {
          const eid = ((e.data || {}) as OrgEdgeData).edgeId || e.id;
          if (eid !== id) return e;
          const dd = (e.data || {}) as OrgEdgeData;
          const next: Edge = { ...e };
          if ('sourceHandle' in patch) next.sourceHandle = patch.sourceHandle || undefined;
          if ('targetHandle' in patch) next.targetHandle = patch.targetHandle || undefined;
          if (patch.sourceMemberId !== undefined || patch.sourceType !== undefined) {
            const cur = e.source ? parseEndpoint(e.source) : { id: '', type: 'member' as const };
            const st = patch.sourceType ?? cur.type;
            const sid = patch.sourceMemberId ?? cur.id;
            next.source =
              st === 'team' ? `team-box-${sid}` : st === 'junction' ? `junction-${sid}` : sid;
          }
          if (patch.targetMemberId !== undefined || patch.targetType !== undefined) {
            const cur = e.target ? parseEndpoint(e.target) : { id: '', type: 'member' as const };
            const tt = patch.targetType ?? cur.type;
            const tid = patch.targetMemberId ?? cur.id;
            next.target =
              tt === 'team' ? `team-box-${tid}` : tt === 'junction' ? `junction-${tid}` : tid;
          }
          const nextData: OrgEdgeData = { ...dd };
          if (patch.waypoints !== undefined) nextData.waypoints = patch.waypoints;
          if (patch.style !== undefined) nextData.lineStyle = patch.style;
          if ('color' in patch) nextData.color = patch.color;
          if ('label' in patch) nextData.label = patch.label;
          if (patch.sourceMemberId !== undefined) nextData.sourceMemberId = patch.sourceMemberId;
          if (patch.targetMemberId !== undefined) nextData.targetMemberId = patch.targetMemberId;
          next.data = nextData;
          if ('color' in patch || patch.thickness !== undefined) {
            const prevStyle = (e.style || {}) as React.CSSProperties & {
              stroke?: string;
              strokeWidth?: number;
            };
            next.style = {
              ...prevStyle,
              stroke: 'color' in patch ? patch.color || 'var(--tx)' : prevStyle.stroke,
              strokeWidth: patch.thickness !== undefined ? patch.thickness : prevStyle.strokeWidth,
            };
          }
          return next;
        })
      );
      session.noteEdgeUpdated(id);
    },
    [setEdges, session]
  );

  const sessionDeleteEdge = useCallback(
    async (id: string) => {
      const e = edges.find((ed) => (((ed.data || {}) as OrgEdgeData).edgeId || ed.id) === id);
      if (e && e.source && e.target) {
        const src = parseEndpoint(e.source);
        const tgt = parseEndpoint(e.target);
        // Reports-to MIRROR (moved in from the old network onDeleteEdge
        // handler): deleting the edge that IS the target's current
        // manager promotes the next remaining member-to-member parent,
        // or detaches to top-level. Main-only — see sessionCreateEdge's
        // mirror block above for why.
        if (isMain && src.type === 'member' && tgt.type === 'member') {
          const target = members.find((m) => m.id === tgt.id);
          if (target && effReportsTo(target) === src.id) {
            const remaining = edges.filter((ed) => {
              if (ed === e) return false;
              const dd = (ed.data || {}) as OrgEdgeData;
              const eid2 = dd.edgeId || ed.id;
              if (eid2 === id || !ed.source || !ed.target) return false;
              const p = parseEndpoint(ed.target);
              const s = parseEndpoint(ed.source);
              return p.type === 'member' && p.id === tgt.id && s.type === 'member';
            });
            const promoted = remaining[0] ? parseEndpoint(remaining[0].source!).id : undefined;
            session.setReportsTo(tgt.id, promoted || '__root__');
          }
        }
      }
      setEdges((prev) =>
        prev.filter((ed) => (((ed.data || {}) as OrgEdgeData).edgeId || ed.id) !== id)
      );
      session.noteEdgeDeleted(id);
    },
    [edges, members, effReportsTo, session, setEdges, isMain]
  );

  const sessionCreateJunction = useCallback(
    async (x: number, y: number): Promise<OrgChartJunction | undefined> => {
      const id = crypto.randomUUID();
      const now = new Date().toISOString();
      setNodes((prev) => [...prev, buildJunctionNode({ id, x, y })]);
      session.noteJunctionCreated(id);
      return { id, x, y, createdAt: now, updatedAt: now };
    },
    [setNodes, session, buildJunctionNode]
  );

  const sessionDeleteJunction = useCallback(
    async (id: string) => {
      removeNodeAndCascadeEdges(`junction-${id}`);
      session.noteJunctionDeleted(id);
    },
    [removeNodeAndCascadeEdges, session]
  );

  const undo = useUndo({
    onSavePositions: applyPositionsLocal,
    onCreateEdge: sessionCreateEdge,
    onUpdateEdge: sessionUpdateEdge,
    onDeleteEdge: sessionDeleteEdge,
    onCreateJunction: sessionCreateJunction,
    onDeleteJunction: sessionDeleteJunction,
    onCreateNote: sessionCreateNote,
    onUpdateNote: sessionUpdateNote,
    onDeleteNote: sessionDeleteNote,
  });

  const edgesForSnap = useMemo(
    () =>
      edges
        .filter((e) => e.type === 'org')
        .map((e) => ({ waypoints: ((e.data || {}) as OrgEdgeData).waypoints }) as OrgChartEdge),
    [edges]
  );

  const dragAndSnap = useNodeDragAndSnap({
    resolvePosition: resolveMemberPosition,
    teams,
    teamAssignments,
    orgChartEdges: edgesForSnap,
    onSavePositions: applyPositionsLocal,
    onUpdateJunction: applyJunctionPositionLocal,
    onUpdateNotePosition: applyNotePositionLocal,
    pushUndo: undo.pushUndo,
    broadcastDrag: guardedBroadcastDrag,
    localDraggingIds,
    snapEnabledRef,
    snapTargetsRef,
    // Single write channel fix (regression, 2026-08-18) — the hook must
    // never write positions through `useReactFlow().setNodes` (a second,
    // store-only channel invisible to this component's own controlled
    // `nodes` state); every drag/snap position write flows through THIS
    // setter so nothing can silently revert it. See the hook's own
    // header comment for the full mechanism.
    setNodes,
  });
  const { getNodes, rfSetNodes } = dragAndSnap;

  // ─── Person-card <-> team-box membership staging + team-box <-> team-box
  // reparent staging (Team Management UX Overhaul, 2026-08-18). Main board
  // ONLY — on a custom board these gestures stay purely visual (existing
  // behavior, untouched). Deliberately does NOT thread through
  // useNodeDragAndSnap's internals (snap/undo/multiplayer/broadcast stay
  // exactly as shipped) — this wraps the hook's three handlers at the call
  // site and does its own independent hit-testing on drag start/stop,
  // which is all "is this card's/box's rect inside a team-box rect" needs.
  // Everything below only STAGES via `session` — `orgchart-commit` (Save)
  // stays the only persistence path; Cancel discards it like any other
  // staged change. ───

  /** Deepest (most-nested) team-box whose rect contains a node's center,
   *  or null if the node isn't inside any currently-visible box. Depth
   *  wins over area so dropping into a nested child's region attributes
   *  to the child, never every ancestor box it's also geometrically
   *  inside of. */
  function findContainingTeamBoxId(
    rect: { x: number; y: number; w: number; h: number },
    allNodes: Node[],
    excludeTeamIds?: Set<string>
  ): string | null {
    const cx = rect.x + rect.w / 2;
    const cy = rect.y + rect.h / 2;
    let best: { teamId: string; depth: number; area: number } | null = null;
    for (const n of allNodes) {
      if (n.type !== 'teamBox') continue;
      const teamId = n.id.replace(/^team-box-/, '');
      if (excludeTeamIds?.has(teamId)) continue;
      const dd = (n.data || {}) as { width?: number; height?: number; depth?: number };
      const bw = n.measured?.width ?? dd.width ?? 0;
      const bh = n.measured?.height ?? dd.height ?? 0;
      if (
        cx < n.position.x ||
        cx > n.position.x + bw ||
        cy < n.position.y ||
        cy > n.position.y + bh
      )
        continue;
      const depth = dd.depth ?? 0;
      const area = bw * bh;
      if (!best || depth > best.depth || (depth === best.depth && area < best.area)) {
        best = { teamId, depth, area };
      }
    }
    return best?.teamId ?? null;
  }

  /** Effective membership = base `teamAssignments` truth, adjusted by
   *  whatever this session has already staged (so re-dragging a card
   *  back and forth reads correctly mid-session, before any Save). */
  function isEffectivelyMember(memberId: string, teamId: string): boolean {
    const key = `${teamId}::${memberId}`;
    if (session.membershipRemoves.has(key)) return false;
    if (session.membershipAdds.has(key)) return true;
    return teamAssignments.some((a) => a.teamId === teamId && a.teamMemberId === memberId);
  }

  function teamLabel(teamId: string): string {
    return teams.find((t) => t.id === teamId)?.name || 'team';
  }

  // Which team's box a dragged person card STARTED inside, captured at
  // drag-start so drag-stop can diff "started here, ended there" — the
  // ONLY thing this needs from the drag gesture itself; the box rects at
  // drag-stop are read live via dragAndSnap.getNodes().
  const membershipDragStartTeamId = useRef<string | null>(null);
  // Live drop-target hint, for the "+ Add to <team>" / "Nest under
  // <team>" legibility affordance below — computed fresh on EVERY
  // onNodeDrag frame from the callback's OWN `node` parameter (always
  // accurate — React Flow calls this synchronously per frame) rather
  // than the controlled `nodes` array this component otherwise reads:
  // that array can visibly lag a fast synthetic/real drag gesture by
  // more than the CSS transition ever recovers from mid-gesture (found
  // via the functional-test harness — a hint computed off `nodes`
  // could settle on the wrong box, or never resolve, for the whole
  // hold before release). Every box's OWN rect still comes from the
  // normal `newBoxes` derivation below — only "which node is the
  // user's hand currently on" needs the frame-accurate source.
  const [dragHoverHint, setDragHoverHint] = useState<{
    teamId: string;
    kind: 'add' | 'remove' | 'reparent';
  } | null>(null);

  const wrappedOnNodeDragStart: OnNodeDrag = useCallback(
    (event, node, allNodes) => {
      dragAndSnap.onNodeDragStart(event, node, allNodes);
      if (!isMain || node.type !== 'person') return;
      const w = node.measured?.width ?? NODE_WIDTH;
      const h = node.measured?.height ?? NODE_HEIGHT;
      membershipDragStartTeamId.current = findContainingTeamBoxId(
        { x: node.position.x, y: node.position.y, w, h },
        getNodes()
      );
    },

    [dragAndSnap, isMain, getNodes]
  );

  const wrappedOnNodeDrag: OnNodeDrag = useCallback(
    (event, node, allNodes) => {
      dragAndSnap.onNodeDrag(event, node, allNodes);
      if (!isMain || !editable) return;
      const liveNodes = getNodes();
      let next: { teamId: string; kind: 'add' | 'remove' | 'reparent' } | null = null;
      if (node.type === 'person') {
        const w = node.measured?.width ?? NODE_WIDTH;
        const h = node.measured?.height ?? NODE_HEIGHT;
        const hoverTeamId = findContainingTeamBoxId(
          { x: node.position.x, y: node.position.y, w, h },
          liveNodes
        );
        if (hoverTeamId && !isEffectivelyMember(node.id, hoverTeamId)) {
          next = { teamId: hoverTeamId, kind: 'add' };
        } else {
          const startTeamId = membershipDragStartTeamId.current;
          if (
            startTeamId &&
            startTeamId !== hoverTeamId &&
            isEffectivelyMember(node.id, startTeamId)
          ) {
            next = { teamId: startTeamId, kind: 'remove' };
          }
        }
      } else if (node.type === 'teamBox') {
        const teamId = node.id.replace(/^team-box-/, '');
        const dd = (node.data || {}) as { width?: number; height?: number };
        const w = node.measured?.width ?? dd.width ?? 0;
        const h = node.measured?.height ?? dd.height ?? 0;
        const subtreeIds = collectSubtreeTeamIds(teamId, teams);
        const hoverParentId = findContainingTeamBoxId(
          { x: node.position.x, y: node.position.y, w, h },
          liveNodes,
          subtreeIds
        );
        if (hoverParentId) next = { teamId: hoverParentId, kind: 'reparent' };
      }
      setDragHoverHint((prev) =>
        prev?.teamId === next?.teamId && prev?.kind === next?.kind ? prev : next
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dragAndSnap, isMain, editable, getNodes, teams, teamAssignments, session]
  );

  const wrappedOnNodeDragStop: OnNodeDrag = useCallback(
    (event, node, allNodes) => {
      dragAndSnap.onNodeDragStop(event, node, allNodes);
      setDragHoverHint(null);
      if (!isMain || !editable) return;

      if (node.type === 'person') {
        const w = node.measured?.width ?? NODE_WIDTH;
        const h = node.measured?.height ?? NODE_HEIGHT;
        const endTeamId = findContainingTeamBoxId(
          { x: node.position.x, y: node.position.y, w, h },
          getNodes()
        );
        const startTeamId = membershipDragStartTeamId.current;
        membershipDragStartTeamId.current = null;
        if (endTeamId === startTeamId) return; // still in the same box (or same "outside everything") — plain move

        const personName = (node.data as PersonNodeData | undefined)?.member?.name || 'Member';
        const removed = startTeamId && isEffectivelyMember(node.id, startTeamId);
        const added = endTeamId && !isEffectivelyMember(node.id, endTeamId);
        if (removed) session.stageMembershipRemove(startTeamId, node.id);
        if (added) session.stageMembershipAdd(endTeamId, node.id);
        // ONE toast even when both apply (a card dragged straight from
        // one team's box into another's) — two toast() calls in the
        // same synchronous tick can collide on the toast list's
        // Date.now()-based key (app.tsx), a real defect this surfaced.
        if (removed && added) {
          toast(
            `${personName} moved from ${teamLabel(startTeamId)} to ${teamLabel(endTeamId)} on save`
          );
        } else if (removed) {
          toast(`${personName} will be removed from ${teamLabel(startTeamId)} on save`);
        } else if (added) {
          toast(`${personName} will be added to ${teamLabel(endTeamId)} on save`);
        }
        return;
      }

      if (node.type === 'teamBox') {
        const teamId = node.id.replace(/^team-box-/, '');
        const dd = (node.data || {}) as { width?: number; height?: number };
        const w = node.measured?.width ?? dd.width ?? 0;
        const h = node.measured?.height ?? dd.height ?? 0;
        const subtreeIds = collectSubtreeTeamIds(teamId, teams);
        const newParentId = findContainingTeamBoxId(
          { x: node.position.x, y: node.position.y, w, h },
          getNodes(),
          subtreeIds
        );
        const currentParentId = teams.find((t) => t.id === teamId)?.parentTeamId || null;
        const effectiveCurrentParentId = session.teamParentChanges.has(teamId)
          ? session.teamParentChanges.get(teamId)!
          : currentParentId;
        if (newParentId === effectiveCurrentParentId) return; // dropped back where it started (or still outside everything) — plain move

        session.stageTeamParentChange(teamId, newParentId);
        toast(
          newParentId
            ? `${teamLabel(teamId)} will nest under ${teamLabel(newParentId)} on save`
            : `${teamLabel(teamId)} will un-nest on save`
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dragAndSnap, isMain, editable, getNodes, session, teams, teamAssignments, toast]
  );

  // ─── Keep the context-shared snap-target list current whenever
  // geometry settles (replaces the old window bridge). Reads the
  // CURRENT `edges` state (not the props array) so in-session
  // waypoint edits feed snapping too. ───
  useEffect(() => {
    const xs: number[] = [];
    const ys: number[] = [];
    for (const n of nodes) {
      if (n.type !== 'person') continue;
      const nw = n.measured?.width ?? NODE_WIDTH;
      const nh = n.measured?.height ?? NODE_HEIGHT;
      xs.push(n.position.x, n.position.x + nw / 2, n.position.x + nw);
      ys.push(n.position.y, n.position.y + nh / 2, n.position.y + nh);
    }
    for (const e of edges) {
      if (e.type !== 'org') continue;
      for (const wp of ((e.data || {}) as OrgEdgeData).waypoints || []) {
        xs.push(wp.x);
        ys.push(wp.y);
      }
    }
    snapTargetsRef.current = { xs, ys };
  }, [nodes, edges]);

  // ─── Live team-box backdrops (Nested Team Containers, People
  // Rebuild — containment comes from the EXISTING `teams.parentTeamId`
  // field, no migration). Bottom-up: a parent's bounds = its own
  // direct member cards UNION its visible child teams' ALREADY-
  // COMPUTED boxes, plus padding — so nesting composes for free by
  // processing teams deepest-first. Z-order = containment depth,
  // parents LOWEST (still under every person card at zIndex 0) so
  // children always paint above their parent. ───
  useEffect(() => {
    const PAD = 24;
    const HEADER = 14;
    const personNodes = nodes.filter((n) => n.type === 'person');
    const personById = new Map(personNodes.map((n) => [n.id, n]));
    const teamById = new Map(teams.map((t) => [t.id, t]));
    const childrenOf = new Map<string, Team[]>();
    for (const t of teams) {
      if (!t.parentTeamId) continue;
      if (!childrenOf.has(t.parentTeamId)) childrenOf.set(t.parentTeamId, []);
      childrenOf.get(t.parentTeamId)!.push(t);
    }
    // Depth of each team in the containment tree — cycle-guarded (a
    // corrupt parent chain degrades to depth 0 for the offending link
    // rather than recursing forever; TeamsPanel's parent picker already
    // excludes self/descendants so this is defense-in-depth only).
    const depthOf = new Map<string, number>();
    function computeDepth(teamId: string, ancestors: Set<string>): number {
      if (depthOf.has(teamId)) return depthOf.get(teamId)!;
      if (ancestors.has(teamId)) {
        depthOf.set(teamId, 0);
        return 0;
      }
      const pid = teamById.get(teamId)?.parentTeamId;
      if (!pid || !teamById.has(pid)) {
        depthOf.set(teamId, 0);
        return 0;
      }
      const d = computeDepth(pid, new Set(ancestors).add(teamId)) + 1;
      depthOf.set(teamId, d);
      return d;
    }
    for (const t of teams) computeDepth(t.id, new Set());

    // Deepest-first so a parent can read its children's finished rects.
    const order = [...teams].sort((a, b) => (depthOf.get(b.id) ?? 0) - (depthOf.get(a.id) ?? 0));
    const rectByTeamId = new Map<string, { x: number; y: number; w: number; h: number }>();
    const newBoxes: Node[] = [];
    for (const team of order) {
      if (!visibleTeamIds[team.id]) continue;
      const memberIdsInTeam = teamAssignments
        .filter((a) => a.teamId === team.id)
        .map((a) => a.teamMemberId);
      const ownNodes = memberIdsInTeam
        .map((id) => personById.get(id))
        .filter((n): n is Node => !!n);
      const childRects = (childrenOf.get(team.id) || [])
        .filter((c) => visibleTeamIds[c.id])
        .map((c) => rectByTeamId.get(c.id))
        .filter((r): r is { x: number; y: number; w: number; h: number } => !!r);

      // Same "no box around one lone card" rule as before for a plain
      // (non-nesting) team; a team with at least one visible child box
      // is a real container even with 0-1 direct members of its own.
      const threshold = childRects.length > 0 ? 1 : 2;
      if (ownNodes.length + childRects.length < threshold) continue;

      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const n of ownNodes) {
        const w = n.measured?.width ?? NODE_WIDTH;
        const h = n.measured?.height ?? NODE_HEIGHT;
        minX = Math.min(minX, n.position.x);
        minY = Math.min(minY, n.position.y);
        maxX = Math.max(maxX, n.position.x + w);
        maxY = Math.max(maxY, n.position.y + h);
      }
      for (const r of childRects) {
        minX = Math.min(minX, r.x);
        minY = Math.min(minY, r.y);
        maxX = Math.max(maxX, r.x + r.w);
        maxY = Math.max(maxY, r.y + r.h);
      }

      const boxX = minX - PAD;
      const boxY = minY - PAD - HEADER;
      const boxW = maxX - minX + PAD * 2;
      const boxH = maxY - minY + PAD * 2 + HEADER;
      rectByTeamId.set(team.id, { x: boxX, y: boxY, w: boxW, h: boxH });
      const depth = depthOf.get(team.id) ?? 0;
      newBoxes.push({
        id: `team-box-${team.id}`,
        type: 'teamBox',
        // Per-node draggable/connectable omitted — see buildJunctionNode.
        selectable: false,
        focusable: false,
        zIndex: depth - 100,
        position: { x: boxX, y: boxY },
        data: {
          name: team.name,
          color: team.color || 'var(--ac)',
          width: boxW,
          height: boxH,
          depth,
        },
      } as Node);
    }

    // Live drag-hint (Team Management UX Overhaul, 2026-08-18): while a
    // person card or a team box is actively being dragged (Main board,
    // session open), mark whichever box it's currently hovering so the
    // drop target is legible BEFORE release — feedback during an
    // already-started drag, not a new hover-only affordance (list/
    // assignment UIs stay click-primary per docs/DESIGN.md §10.2). A
    // card that's simply moving WITHIN the box it already belongs to
    // gets no hint at all — joining/leaving a team must read as a
    // distinct, deliberate gesture, never a side effect of an ordinary
    // reposition. `dragHoverHint` is computed frame-accurately by
    // `wrappedOnNodeDrag` (see above) — this just paints whichever box
    // it names.
    if (dragHoverHint) {
      const hintBox = newBoxes.find((b) => b.id === `team-box-${dragHoverHint.teamId}`);
      if (hintBox) {
        (hintBox.data as { dragHint?: 'add' | 'remove' | 'reparent' }).dragHint =
          dragHoverHint.kind;
      }
    }

    const sig = (boxes: Node[]) =>
      boxes
        .map((b) => {
          const dd = (b.data || {}) as {
            width?: number;
            height?: number;
            name?: string;
            color?: string;
            depth?: number;
            dragHint?: string;
          };
          return `${b.id}:${b.position.x}:${b.position.y}:${dd.width}:${dd.height}:${dd.name}:${dd.color}:${dd.depth}:${dd.dragHint || ''}`;
        })
        .sort()
        .join('|');
    const existing = nodes.filter((n) => n.type === 'teamBox');
    if (sig(existing) === sig(newBoxes)) return;
    setNodes((prev) => {
      const without = prev.filter((n) => n.type !== 'teamBox');
      return [...newBoxes, ...without];
    });
  }, [nodes, visibleTeamIds, teamAssignments, teams, setNodes, editable, isMain, dragHoverHint]);

  // ─── Hover-to-trace + endpoint flash ───
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);
  const handleEdgeHoverChange = useCallback((edgeId: string, hovering: boolean) => {
    setHoveredEdgeId((prev) => (hovering ? edgeId : prev === edgeId ? null : prev));
  }, []);
  const [flashingEdgeId, setFlashingEdgeId] = useState<string | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleEndpointClick = useCallback((edgeId: string) => {
    setFlashingEdgeId(edgeId);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlashingEdgeId(null), 700);
  }, []);

  // ─── Line-crossing hops ───
  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const resolveHandlePoint = useCallback(
    (nodeId: string, handle: string | null | undefined): { x: number; y: number } | null => {
      const n = nodeById.get(nodeId);
      if (!n) return null;
      const dd = (n.data || {}) as { width?: number; height?: number };
      const w = n.measured?.width ?? (n.type === 'junction' ? 16 : (dd.width ?? NODE_WIDTH));
      const h = n.measured?.height ?? (n.type === 'junction' ? 16 : (dd.height ?? NODE_HEIGHT));
      const cx = n.position.x + w / 2;
      const cy = n.position.y + h / 2;
      switch (handle) {
        case 'top':
          return { x: cx, y: n.position.y };
        case 'bottom':
          return { x: cx, y: n.position.y + h };
        case 'left':
          return { x: n.position.x, y: cy };
        case 'right':
          return { x: n.position.x + w, y: cy };
        default:
          return { x: cx, y: cy };
      }
    },
    [nodeById]
  );
  const toPosition = (handle: string | null | undefined, fallback: Position): Position => {
    if (handle === 'top') return Position.Top;
    if (handle === 'bottom') return Position.Bottom;
    if (handle === 'left') return Position.Left;
    if (handle === 'right') return Position.Right;
    return fallback;
  };

  // ─── Line geometry obstacle set (People Rebuild, Josh grant
  // 2026-08-17) — every OTHER node's bounding rect: person + junction
  // full boxes, and ONLY the team-box HEADER strip (the tinted body is
  // deliberately not an obstacle — lines may cross it). The general
  // A*-obstacle-avoiding router that used to be this set's main
  // consumer is RETIRED (People Rebuild, Josh law 2026-08-18: "zero
  // turns = one straight line = default"); this set now feeds ONLY the
  // trunk-and-bus tree router's bus-nudge check below. ───
  const HEADER_STRIP = 14; // must match the "Live team-box backdrops" HEADER constant above
  const routingObstacles = useMemo(() => {
    const out: { nodeId: string; rect: ObstacleRect }[] = [];
    for (const n of nodes) {
      if (n.type === 'person') {
        const w = n.measured?.width ?? NODE_WIDTH;
        const h = n.measured?.height ?? NODE_HEIGHT;
        out.push({ nodeId: n.id, rect: { x: n.position.x, y: n.position.y, width: w, height: h } });
      } else if (n.type === 'junction') {
        out.push({
          nodeId: n.id,
          rect: { x: n.position.x, y: n.position.y, width: 16, height: 16 },
        });
      } else if (n.type === 'teamBox') {
        const dd = (n.data || {}) as { width?: number; height?: number };
        out.push({
          nodeId: n.id,
          rect: { x: n.position.x, y: n.position.y, width: dd.width ?? 0, height: HEADER_STRIP },
        });
      }
    }
    return out;
  }, [nodes]);

  // ─── Classic trunk-and-bus tree routing (People Rebuild, Josh
  // incident 2026-08-17: "the auto-routes are trash — I expect
  // CLASSIC ORG-CHART lines"). Runs BEFORE the plain straight-segment
  // fallback below — an edge this pattern claims never falls through
  // to it. Scoped to MEMBER (person) nodes only: `personBoundsById` is
  // filtered to person nodes, so an edge touching a junction or team
  // box simply isn't in it and falls straight through to the plain
  // fallback. ───
  const personBoundsById = useMemo(() => {
    const m = new Map<string, ObstacleRect>();
    for (const n of nodes) {
      if (n.type !== 'person') continue;
      const w = n.measured?.width ?? NODE_WIDTH;
      const h = n.measured?.height ?? NODE_HEIGHT;
      m.set(n.id, { x: n.position.x, y: n.position.y, width: w, height: h });
    }
    return m;
  }, [nodes]);

  const treeRoutesByEdgeId = useMemo(() => {
    const candidates: TreeEdgeInput[] = [];
    for (const e of edges) {
      if (e.type !== 'org' || !e.source || !e.target) continue;
      const ed = (e.data || {}) as OrgEdgeData;
      if ((ed.waypoints || []).length > 0) continue; // manual — the org pattern never touches it
      if (!personBoundsById.has(e.source) || !personBoundsById.has(e.target)) continue;
      candidates.push({ id: ed.edgeId || e.id, source: e.source, target: e.target });
    }
    return computeTreeRoutes(candidates, personBoundsById, routingObstacles);
  }, [edges, personBoundsById, routingObstacles]);

  /** edgeId -> family key (the shared parent's node id), for edges the
   *  bus router actually produced — feeds the hop-detection exclusion
   *  below so siblings sharing a trunk/bus never bridge-arc over each
   *  other. */
  const treeFamilyOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of edges) {
      if (e.type !== 'org' || !e.source) continue;
      const ed = (e.data || {}) as OrgEdgeData;
      const eid = ed.edgeId || e.id;
      if (treeRoutesByEdgeId.has(eid)) m.set(eid, e.source);
    }
    return m;
  }, [edges, treeRoutesByEdgeId]);

  /** Per-edge computed auto-route (mid-points only, source/target
   *  excluded) cache. A plain (non-tree-routed) auto edge contributes
   *  the shared `EMPTY_MID` reference here — the actual near-axis-snap
   *  / fewest-turn L-Z decision needs the TRUE resolved anchor points
   *  and Position (`buildEdgePoints`), which only exist where an edge
   *  is actually rendered (`edgeGeoms` right below, and again inside
   *  OrgEdgeV2 for the live stroke) — nothing to precompute here for
   *  those. Only the trunk-and-bus tree route's per-render recompute
   *  needs `samePts` dedup, to keep the live-injection effect's
   *  `dd.autoWaypoints === nextAuto` no-op guard working when a
   *  family's geometry hasn't actually changed. */
  const autoRouteCacheRef = useRef<Map<string, Pt[]>>(new Map());
  const autoRoutesByEdgeId = useMemo(() => {
    const out = new Map<string, Pt[]>();
    for (const e of edges) {
      if (e.type !== 'org' || !e.source || !e.target) continue;
      const ed = (e.data || {}) as OrgEdgeData;
      if ((ed.waypoints || []).length > 0) continue; // manual — router doesn't touch it
      const eid = ed.edgeId || e.id;

      // Classic trunk-and-bus tree route claims this edge first —
      // cheap math, recomputed fresh every render (even mid-drag, per
      // brief: "no caching gymnastics needed").
      const treeFull = treeRoutesByEdgeId.get(eid);
      if (treeFull) {
        const mid = treeFull.slice(1, -1);
        const cachedTree = autoRouteCacheRef.current.get(eid);
        const nextTree = samePts(mid, cachedTree) ? cachedTree! : mid;
        autoRouteCacheRef.current.set(eid, nextTree);
        out.set(eid, nextTree);
        continue;
      }

      // Every other auto edge (junction endpoints, team endpoints,
      // child-above/level, cross-links) — every plain auto edge just
      // shares one empty array; see the doc comment above for why.
      out.set(eid, EMPTY_MID);
    }
    return out;
  }, [edges, treeRoutesByEdgeId]);

  const edgeGeoms = useMemo(() => {
    const out: EdgeGeometry[] = [];
    for (const e of edges) {
      if (e.type !== 'org') continue;
      const ed = (e.data || {}) as OrgEdgeData;
      const eid = ed.edgeId || e.id;
      // Tree-routed edges force bottom/top anchors — the org pattern
      // owns the anchors while auto (brief, explicit); whatever
      // sourceHandle/targetHandle happens to be stored is ignored here
      // so this stays in lockstep with what React Flow will actually
      // draw once the live-injection effect forces the same values
      // onto the RF edge object below.
      const isTreeRouted = treeRoutesByEdgeId.has(eid);
      const effSourceHandle = isTreeRouted ? 'bottom' : e.sourceHandle;
      const effTargetHandle = isTreeRouted ? 'top' : e.targetHandle;
      const sourcePt = resolveHandlePoint(e.source, effSourceHandle);
      const targetPt = resolveHandlePoint(e.target, effTargetHandle);
      if (!sourcePt || !targetPt) continue;
      const midPoints =
        (ed.waypoints || []).length > 0 ? ed.waypoints! : (autoRoutesByEdgeId.get(eid) ?? []);
      const allPts = buildEdgePoints(
        sourcePt,
        targetPt,
        midPoints,
        endpointFlex(e.source),
        endpointFlex(e.target),
        toPosition(effSourceHandle, Position.Bottom),
        toPosition(effTargetHandle, Position.Top)
      );
      const polyline = getEdgePolyline(allPts);
      out.push({ edgeId: eid, polyline });
    }
    return out;
  }, [edges, resolveHandlePoint, autoRoutesByEdgeId, treeRoutesByEdgeId]);
  const edgeGeomSig = useMemo(() => geometrySignature(edgeGeoms), [edgeGeoms]);
  const hopsMap = useMemo(
    () => computeEdgeHops(edgeGeoms, treeFamilyOf),
    [edgeGeomSig, treeFamilyOf]
  );

  // ─── Spawn a junction on a line at a given point (People Rebuild,
  // Josh law 2026-08-17: menu-driven only — "Add junction here" on the
  // line's right-click menu; the old drag-off-the-line branch handle
  // is gone). ───
  const handleSpawnJunction = useCallback(
    async (edgeId: string, x: number, y: number) => {
      const rf = edges.find((ed) => (((ed.data || {}) as OrgEdgeData).edgeId || ed.id) === edgeId);
      const edge = rf ? edgeFromRF(rf, edgeId) : null;
      if (!edge) return;
      const junction = await sessionCreateJunction(x, y);
      if (!junction) return;
      const now = new Date().toISOString();
      const newEdge: OrgChartEdge = {
        id: crypto.randomUUID(),
        sourceMemberId: junction.id,
        sourceType: 'junction',
        targetMemberId: edge.targetMemberId,
        targetType: edge.targetType,
        targetHandle: edge.targetHandle,
        style: edge.style,
        color: edge.color,
        createdAt: now,
        updatedAt: now,
      };
      await sessionCreateEdge(newEdge);
      const prevRetarget = {
        targetMemberId: edge.targetMemberId,
        targetType: edge.targetType,
        waypoints: edge.waypoints,
      };
      const nextRetarget = {
        targetMemberId: junction.id,
        targetType: 'junction' as const,
        targetHandle: undefined,
        waypoints: [],
      };
      await sessionUpdateEdge(edgeId, nextRetarget);
      undo.pushUndo({
        kind: 'junction-spawn',
        junction,
        newEdge,
        retargetedEdgeId: edgeId,
        prevRetarget,
        nextRetarget,
      });
    },
    [edges, sessionCreateJunction, sessionCreateEdge, sessionUpdateEdge, undo]
  );

  // ─── Waypoint editing — every call applies IMMEDIATELY (no debounce
  // on the data write, since there is no round-trip cost to avoid
  // anymore — this is what kills defect #4, "lost on app close"). Only
  // the UNDO CHECKPOINT is lightly debounced, so Ctrl+Z undoes a whole
  // drag instead of one pixel. ───
  const waypointUndoTimer = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const waypointBeforeRef = useRef<Record<string, { x: number; y: number }[] | undefined>>({});
  const handleWaypointsChange = useCallback(
    (edgeId: string, next: { x: number; y: number }[]) => {
      if (!(edgeId in waypointBeforeRef.current)) {
        const e = edges.find((ed) => (((ed.data || {}) as OrgEdgeData).edgeId || ed.id) === edgeId);
        waypointBeforeRef.current[edgeId] = e
          ? ((e.data || {}) as OrgEdgeData).waypoints
          : undefined;
      }
      setEdges((prev) =>
        prev.map((e) => {
          const eid = ((e.data || {}) as OrgEdgeData).edgeId || e.id;
          if (eid !== edgeId) return e;
          const dd = (e.data || {}) as OrgEdgeData;
          return { ...e, data: { ...dd, waypoints: next } };
        })
      );
      session.noteEdgeUpdated(edgeId);
      if (waypointUndoTimer.current[edgeId]) clearTimeout(waypointUndoTimer.current[edgeId]);
      waypointUndoTimer.current[edgeId] = setTimeout(() => {
        const before = waypointBeforeRef.current[edgeId];
        delete waypointBeforeRef.current[edgeId];
        delete waypointUndoTimer.current[edgeId];
        undo.pushUndo({
          kind: 'edge-patch',
          edgeId,
          prev: { waypoints: before },
          next: { waypoints: next },
        });
      }, 250);
    },
    [edges, setEdges, session, undo]
  );

  // ─── Inject live callbacks + hover/hop state into edge.data ───
  useEffect(() => {
    setEdges((prev) =>
      prev.map((e) => {
        if (e.type !== 'org') return e;
        const dd = (e.data || {}) as OrgEdgeData;
        const edgeId = dd.edgeId || e.id;
        const nextHops = hopsMap.get(edgeId);
        // Render-only auto-route (see OrgEdgeData.autoWaypoints) — a
        // cache hit above returns the SAME array reference, so an
        // edge whose route didn't change makes this whole branch a
        // no-op and never marks the session dirty (nothing here calls
        // session.note*, matching hopsBySeg/highlight/flashing).
        const nextAuto = autoRoutesByEdgeId.get(edgeId);
        const nextHighlight: OrgEdgeData['highlight'] = hoveredEdgeId
          ? edgeId === hoveredEdgeId
            ? 'active'
            : 'dimmed'
          : 'none';
        const nextFlashing = flashingEdgeId === edgeId;
        // Tree-routed edges force bottom/top anchors ON THE EDGE
        // OBJECT ITSELF (not just `data`) — React Flow computes the
        // sourceX/sourceY it hands OrgEdgeV2 from the Edge's actual
        // sourceHandle/targetHandle, so that's the only place this can
        // be forced from. This is render-only in the SAME sense
        // `autoWaypoints` is: while an edge stays auto+tree-routed
        // (empty waypoints, child below parent) this recomputes to the
        // identical 'bottom'/'top' every render regardless of what's
        // in the DB, so even if a later save happens to persist it
        // (edgeFromRF reads this field), the next load re-derives the
        // exact same value — never a stale/observable drift. The
        // moment the edge stops being tree-routed (node moved, or
        // segment-drag converts it to manual) this stops overriding
        // and the edge's own stored/dragged handles take back over,
        // same as any other edge today.
        const isTreeRouted = treeRoutesByEdgeId.has(edgeId);
        const nextSourceHandle = isTreeRouted ? 'bottom' : e.sourceHandle;
        const nextTargetHandle = isTreeRouted ? 'top' : e.targetHandle;
        if (
          dd.onWaypointsChange === handleWaypointsChange &&
          dd.canEdit === editable &&
          dd.hopsBySeg === nextHops &&
          dd.autoWaypoints === nextAuto &&
          dd.highlight === nextHighlight &&
          dd.flashing === nextFlashing &&
          dd.onHoverChange === handleEdgeHoverChange &&
          dd.onEndpointClick === handleEndpointClick &&
          e.sourceHandle === nextSourceHandle &&
          e.targetHandle === nextTargetHandle
        )
          return e;
        return {
          ...e,
          sourceHandle: nextSourceHandle,
          targetHandle: nextTargetHandle,
          data: {
            ...dd,
            onWaypointsChange: editable ? handleWaypointsChange : undefined,
            canEdit: editable,
            hopsBySeg: nextHops,
            autoWaypoints: nextAuto,
            highlight: nextHighlight,
            flashing: nextFlashing,
            onHoverChange: handleEdgeHoverChange,
            onEndpointClick: handleEndpointClick,
          },
        };
      })
    );
  }, [
    handleWaypointsChange,
    editable,
    setEdges,
    hopsMap,
    autoRoutesByEdgeId,
    treeRoutesByEdgeId,
    hoveredEdgeId,
    flashingEdgeId,
    handleEdgeHoverChange,
    handleEndpointClick,
  ]);

  // ─── Hover-to-trace: glow endpoint nodes ───
  const highlightedNodeIds = useMemo(() => {
    if (!hoveredEdgeId) return null;
    const e = edges.find(
      (ed) => (((ed.data || {}) as OrgEdgeData).edgeId || ed.id) === hoveredEdgeId
    );
    if (!e) return null;
    return new Set([e.source, e.target]);
  }, [hoveredEdgeId, edges]);
  useEffect(() => {
    setNodes((prev) =>
      prev.map((n) => {
        if (n.type !== 'person' && n.type !== 'junction') return n;
        const glow = highlightedNodeIds?.has(n.id) ?? false;
        const dd = (n.data || {}) as { glow?: boolean };
        if ((dd.glow ?? false) === glow) return n;
        return { ...n, data: { ...dd, glow } };
      })
    );
  }, [highlightedNodeIds, setNodes]);

  // ─── Re-derive nodes/edges from PROPS only when NOT in a session —
  // the guard that fixes defect #2 (a cosmetic-only prop change, e.g.
  // toggling a card-style checkbox, used to snap a just-dragged node
  // back because this effect didn't distinguish "editing right now"
  // from "any prop changed"). ───
  const prevChartKey = useRef('');
  useEffect(() => {
    if (session.active) return;
    const chartKey = computeChartKey();
    if (chartKey !== prevChartKey.current) {
      prevChartKey.current = chartKey;
      setNodes(initialNodes);
      setEdges(initialEdges);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialNodes, initialEdges, session.active]);

  // ─── External-change notice: a sync arrived mid-session. Chart state
  // itself is untouched (guarded above) — this only flags it. ───
  const sessionStartSignatureRef = useRef('');
  useEffect(() => {
    if (!session.active) return;
    const sig = computeChartKey();
    if (sessionStartSignatureRef.current === '') {
      sessionStartSignatureRef.current = sig;
      return;
    }
    if (sig !== sessionStartSignatureRef.current) session.noteExternalChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.active, computeChartKey]);

  // ─── Fit-on-load ───
  const didInitialFitRef = useRef(false);
  const [rfInitialized, setRfInitialized] = useState(false);

  const isValidConnection: IsValidConnection = useCallback(
    (connection) => {
      if (!connection.source || !connection.target || connection.source === connection.target)
        return false;
      const parentsOf = new Map<string, string[]>();
      for (const e of edges) {
        if (e.type !== 'org' || !e.source || !e.target) continue;
        const list = parentsOf.get(e.target) || [];
        list.push(e.source);
        parentsOf.set(e.target, list);
      }
      const visited = new Set<string>();
      const stack: string[] = [connection.source];
      while (stack.length > 0) {
        const cur = stack.pop()!;
        if (visited.has(cur)) continue;
        visited.add(cur);
        if (cur === connection.target) return false;
        for (const p of parentsOf.get(cur) || []) stack.push(p);
      }
      return true;
    },
    [edges]
  );

  const onConnect = useCallback(
    async (connection: Connection) => {
      if (!connection.source || !connection.target) return;
      const sourceHandle =
        (connection.sourceHandle as 'top' | 'bottom' | 'left' | 'right' | null) || undefined;
      const targetHandle =
        (connection.targetHandle as 'top' | 'bottom' | 'left' | 'right' | null) || undefined;
      const src = parseEndpoint(connection.source);
      const tgt = parseEndpoint(connection.target);
      if (src.type === 'team' && tgt.type === 'team' && src.id === tgt.id) return;
      const existing = edges.find(
        (e) => e.source === connection.source && e.target === connection.target
      );
      if (existing) {
        const eid = ((existing.data || {}) as OrgEdgeData).edgeId || existing.id;
        await sessionUpdateEdge(eid, { sourceHandle, targetHandle });
        return;
      }
      // Duplicate-creation-path fix (2026-08-19): the `existing` check
      // above only sees what's currently ON the canvas — deleting an edge
      // this session drops it from React Flow's `edges` state immediately
      // (sessionDeleteEdge), so redrawing the SAME connection in the SAME
      // session (reroute-by-disconnect-then-reconnect, or just changing
      // your mind) looked like a brand new relationship. Revive the
      // pending-delete edge under its ORIGINAL id instead of minting a
      // new one — prevents the duplicate at the source rather than
      // relying only on the server-side dedupe safety net.
      if (src.type === 'member' && tgt.type === 'member') {
        const revivable = orgChartEdges.find(
          (oe) =>
            oe.sourceMemberId === src.id &&
            oe.targetMemberId === tgt.id &&
            (oe.sourceType || 'member') === 'member' &&
            (oe.targetType || 'member') === 'member' &&
            session.isEdgePendingDelete(oe.id)
        );
        if (revivable) {
          const revivedEdge: Edge = {
            id: revivable.id,
            source: connection.source,
            target: connection.target,
            sourceHandle,
            targetHandle,
            type: 'org',
            data: {
              edgeId: revivable.id,
              waypoints: revivable.waypoints || [],
              lineStyle: revivable.style || 'solid',
              color: revivable.color,
              label: revivable.label,
              sourceMemberId: revivable.sourceMemberId,
              targetMemberId: revivable.targetMemberId,
              canEdit: editable,
            } as OrgEdgeData,
            style: {
              stroke: revivable.color || 'var(--tx)',
              strokeWidth: revivable.thickness || 1.5,
            },
          };
          setEdges((prev) => [...prev, revivedEdge]);
          session.noteEdgeRevived(revivable.id);
          undo.pushUndo({
            kind: 'edge-create',
            edge: { ...revivable, sourceHandle, targetHandle },
          });
          return;
        }
      }
      const id = crypto.randomUUID();
      const now = new Date().toISOString();
      const newEdge: OrgChartEdge = {
        id,
        sourceMemberId: src.id,
        targetMemberId: tgt.id,
        sourceType: src.type,
        targetType: tgt.type,
        style: 'solid',
        sourceHandle,
        targetHandle,
        createdAt: now,
        updatedAt: now,
      };
      await sessionCreateEdge(newEdge);
      undo.pushUndo({ kind: 'edge-create', edge: newEdge });
    },
    [edges, orgChartEdges, session, editable, setEdges, sessionCreateEdge, sessionUpdateEdge, undo]
  );

  const deleteEdgeWithUndo = useCallback(
    async (edgeId: string) => {
      const rf = edges.find((ed) => (((ed.data || {}) as OrgEdgeData).edgeId || ed.id) === edgeId);
      const full = rf ? edgeFromRF(rf, edgeId) : null;
      await sessionDeleteEdge(edgeId);
      if (full) undo.pushUndo({ kind: 'edge-delete', edge: full });
    },
    [edges, sessionDeleteEdge, undo]
  );

  const updateEdgeWithUndo = useCallback(
    async (edgeId: string, patch: Partial<OrgChartEdge>) => {
      const rf = edges.find((ed) => (((ed.data || {}) as OrgEdgeData).edgeId || ed.id) === edgeId);
      const full = rf ? edgeFromRF(rf, edgeId) : null;
      const prev: Partial<OrgChartEdge> = {};
      const fullRec = full as unknown as Record<string, unknown> | null;
      for (const k of Object.keys(patch) as (keyof OrgChartEdge)[]) {
        (prev as Record<string, unknown>)[k] = fullRec ? fullRec[k] : undefined;
      }
      await sessionUpdateEdge(edgeId, patch);
      undo.pushUndo({ kind: 'edge-patch', edgeId, prev, next: patch });
    },
    [edges, sessionUpdateEdge, undo]
  );

  // ─── Text-box notes (People Rebuild, Josh grant 2026-08-17) ─────

  const deleteNoteWithUndo = useCallback(
    async (noteId: string) => {
      const rf = getNodes().find((n) => n.id === `note-${noteId}`);
      const full = rf ? noteFromRF(rf, noteId) : null;
      await sessionDeleteNote(noteId);
      if (full) undo.pushUndo({ kind: 'note-delete', note: full });
    },
    [getNodes, sessionDeleteNote, undo]
  );

  /** Junction delete — routed through the "Delete junction" item on the
   *  junction's right-click menu (People Rebuild, Josh law 2026-08-17:
   *  right-click no longer deletes instantly). Same undoable session
   *  delete path the old instant-delete used. */
  const deleteJunctionWithUndo = useCallback(
    (junctionId: string) => {
      const node = getNodes().find((n) => n.id === `junction-${junctionId}`);
      if (!node) return;
      const dd = (node.data || {}) as { label?: string; color?: string };
      const removedEdges = removeNodeAndCascadeEdges(node.id);
      session.noteJunctionDeleted(junctionId);
      const now = new Date().toISOString();
      undo.pushUndo({
        kind: 'junction-delete',
        junction: {
          id: junctionId,
          x: node.position.x,
          y: node.position.y,
          label: dd.label,
          color: dd.color,
          createdAt: now,
          updatedAt: now,
        },
        edges: removedEdges,
      });
    },
    [getNodes, removeNodeAndCascadeEdges, session, undo]
  );

  /** Textarea blur/Enter — diffs against the CURRENT node text (read
   *  via React Flow's own live getter, not the `nodes` state variable,
   *  so this callback's identity stays stable across drag-frame
   *  re-renders — see the injection effect below for why that matters). */
  const handleCommitNoteText = useCallback(
    (noteId: string, text: string) => {
      const rf = getNodes().find((n) => n.id === `note-${noteId}`);
      const dd = (rf?.data || {}) as NoteNodeData;
      const prevText = dd.text ?? '';
      if (prevText !== text) {
        undo.pushUndo({ kind: 'note-patch', noteId, prev: { text: prevText }, next: { text } });
        sessionUpdateNote(noteId, { text });
      }
      setEditingNoteId(null);
    },
    [getNodes, sessionUpdateNote, undo]
  );

  const handleCancelNoteEdit = useCallback(() => setEditingNoteId(null), []);

  // ─── Inject live editing state + callbacks into textBox node.data —
  // same "inject into data" pattern the edges use (edgeId → data.hops/
  // highlight/onWaypointsChange effect below elsewhere): editingNoteId
  // changes mid-session and must reach the matching node WITHOUT
  // retriggering the session-guarded props resync. No-op identity
  // check keeps this from looping against its own setNodes call. ───
  useEffect(() => {
    setNodes((prev) =>
      prev.map((n) => {
        if (n.type !== 'textBox') return n;
        const dd = (n.data || {}) as NoteNodeData;
        const rawId = n.id.replace(/^note-/, '');
        const nextEditing = editingNoteId === rawId;
        if (
          dd.editing === nextEditing &&
          dd.canEdit === editable &&
          dd.onCommitText === handleCommitNoteText &&
          dd.onCancelEdit === handleCancelNoteEdit
        )
          return n;
        return {
          ...n,
          data: {
            ...dd,
            editing: nextEditing,
            canEdit: editable,
            onCommitText: handleCommitNoteText,
            onCancelEdit: handleCancelNoteEdit,
          },
        };
      })
    );
  }, [editingNoteId, editable, setNodes, handleCommitNoteText, handleCancelNoteEdit]);

  const onReconnect = useCallback(
    async (oldEdge: Edge, newConnection: Connection) => {
      const dd = (oldEdge.data || {}) as OrgEdgeData;
      const edgeId = dd.edgeId || oldEdge.id;
      const src = parseEndpoint(newConnection.source!);
      const tgt = parseEndpoint(newConnection.target!);
      await sessionUpdateEdge(edgeId, {
        sourceMemberId: src.id,
        targetMemberId: tgt.id,
        sourceType: src.type,
        targetType: tgt.type,
        sourceHandle:
          (newConnection.sourceHandle as 'top' | 'bottom' | 'left' | 'right' | null) || undefined,
        targetHandle:
          (newConnection.targetHandle as 'top' | 'bottom' | 'left' | 'right' | null) || undefined,
      });
    },
    [sessionUpdateEdge]
  );

  const onEdgeContextMenu = useCallback((event: React.MouseEvent, edge: Edge) => {
    event.preventDefault();
    setEdgeMenu({ edgeId: edge.id, x: event.clientX, y: event.clientY });
  }, []);

  const onEdgeDoubleClick = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      if (!editable) return;
      event.preventDefault();
      event.stopPropagation();
      const dd = (edge.data || {}) as OrgEdgeData;
      setLabelEditor({
        edgeId: edge.id,
        value: dd.label || '',
        x: event.clientX,
        y: event.clientY,
      });
    },
    [editable]
  );

  useEffect(() => {
    if (!edgeMenu && !ctxMenu && !canvasMenu && !noteMenu && !junctionMenu) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-orgchart-menu]')) return;
      // The shared ColorPicker popover portals to document.body — clicks
      // and drags inside it are NOT "outside the menu".
      if (target?.closest('[data-colorpicker-panel]')) return;
      setEdgeMenu(null);
      setCtxMenu(null);
      setCanvasMenu(null);
      setNoteMenu(null);
      setJunctionMenu(null);
    };
    const t = setTimeout(() => document.addEventListener('mousedown', onDocMouseDown), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDocMouseDown);
    };
  }, [edgeMenu, ctxMenu, canvasMenu, noteMenu, junctionMenu]);

  const onNodeContextMenu: NodeMouseHandler = useCallback((event, node) => {
    event.preventDefault();
    if (node.type === 'textBox') {
      setNoteMenu({ noteId: node.id.replace(/^note-/, ''), x: event.clientX, y: event.clientY });
      return;
    }
    if (node.type === 'junction') {
      setJunctionMenu({
        junctionId: node.id.replace(/^junction-/, ''),
        x: event.clientX,
        y: event.clientY,
      });
      return;
    }
    if (node.type === 'teamBox') return;
    setCtxMenu({ memberId: node.id, x: event.clientX, y: event.clientY });
  }, []);

  const onNodeDoubleClick: NodeMouseHandler = useCallback(
    (_event, node) => {
      if (node.type === 'textBox') {
        if (!editable) return;
        setEditingNoteId(node.id.replace(/^note-/, ''));
        return;
      }
      const m = members.find((x) => x.id === node.id);
      if (m) onMemberClick(m);
    },
    [members, onMemberClick, editable]
  );

  const [reactFlowInstance, setReactFlowInstance] = useState<{
    screenToFlowPosition: (p: { x: number; y: number }) => { x: number; y: number };
    fitView: (opts?: { padding?: number; duration?: number }) => void;
  } | null>(null);

  useEffect(() => {
    if (
      !reactFlowInstance ||
      didInitialFitRef.current ||
      !rfInitialized ||
      chartMembers.length === 0
    )
      return;
    let cancelled = false;
    const tryFit = (attempt: number) => {
      if (cancelled || didInitialFitRef.current) return;
      const measured = getNodes().some((n) => (n.measured?.width ?? 0) > 0);
      if (measured || attempt > 24) {
        didInitialFitRef.current = true;
        reactFlowInstance.fitView({ padding: 0.2, duration: 200 });
        return;
      }
      setTimeout(() => tryFit(attempt + 1), 50);
    };
    tryFit(0);
    return () => {
      cancelled = true;
    };
  }, [reactFlowInstance, rfInitialized, chartMembers.length, getNodes]);

  // Free-standing junction at a canvas point (Josh 2026-08-18: junctions
  // are independent anchors — creation must not require an existing line).
  const handleAddJunction = useCallback(
    (screenX: number, screenY: number) => {
      if (!reactFlowInstance) return;
      const flowPos = reactFlowInstance.screenToFlowPosition({ x: screenX, y: screenY });
      void sessionCreateJunction(flowPos.x, flowPos.y).then((j) => {
        if (j) undo.pushUndo({ kind: 'junction-create', junction: j });
      });
    },
    [reactFlowInstance, sessionCreateJunction, undo]
  );

  const handleAddTextBox = useCallback(
    (screenX: number, screenY: number) => {
      if (!reactFlowInstance) return;
      const flowPos = reactFlowInstance.screenToFlowPosition({ x: screenX, y: screenY });
      const id = crypto.randomUUID();
      const now = new Date().toISOString();
      const note: OrgChartNote = {
        id,
        x: flowPos.x,
        y: flowPos.y,
        text: 'Text',
        fontSize: 14,
        createdAt: now,
        updatedAt: now,
      };
      sessionCreateNote(note);
      undo.pushUndo({ kind: 'note-create', note });
      setEditingNoteId(id);
    },
    [reactFlowInstance, sessionCreateNote, undo]
  );

  const handleAddToChart = useCallback(
    (memberId: string, x: number, y: number) => {
      const m = members.find((mm) => mm.id === memberId);
      if (!m) return;
      setNodes((prev) => [...prev, buildPersonNode(m, { x, y })]);
      // Reports-to MIRROR — Main-only (see sessionCreateEdge's mirror
      // block for why); a custom board just needs the placement.
      if (isMain) session.setReportsTo(memberId, '__root__');
      session.markPositionsTouched([memberId]);
    },
    [members, setNodes, buildPersonNode, session, isMain]
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (!editable || !reactFlowInstance) return;
      const raw = event.dataTransfer.getData('application/json');
      if (!raw) return;
      let payload: { type: string; id: string };
      try {
        payload = JSON.parse(raw);
      } catch {
        return;
      }
      if (payload.type === 'member') {
        const m = members.find((x) => x.id === payload.id);
        if (!m) return;
        if (!chartMemberIds.has(m.id)) {
          const flowPos = reactFlowInstance.screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
          });
          handleAddToChart(m.id, flowPos.x, flowPos.y);
        }
      } else if (payload.type === 'team') {
        const flowPos = reactFlowInstance.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });
        const allNodes = getNodes();
        const targetNode = allNodes.find((n) => {
          const nw = n.measured?.width ?? NODE_WIDTH;
          const nh = n.measured?.height ?? NODE_HEIGHT;
          return (
            flowPos.x >= n.position.x &&
            flowPos.x <= n.position.x + nw &&
            flowPos.y >= n.position.y &&
            flowPos.y <= n.position.y + nh
          );
        });
        if (targetNode) {
          const selected = allNodes.filter((n) => n.selected);
          const targets =
            selected.length > 1 && selected.some((n) => n.id === targetNode.id)
              ? selected.map((n) => n.id)
              : [targetNode.id];
          // Team-membership assignment stays an IMMEDIATE write — it's
          // team_assignments (a different table entirely), not part of
          // the commit payload, and was never part of the stale-
          // position defect class this model fixes. Only reachable
          // while a session is open (this handler is gated on
          // `editable`), same as every other chart-drop action.
          for (const id of targets) onTeamAssign(id, payload.id);
        } else {
          // Dropped on empty canvas (not onto a person card) — place
          // the team's container box (Team Management UX Overhaul,
          // item 4): just a visibility toggle (`visibleTeamIds`,
          // already exposed via the Show/Hide-all-boxes menu action),
          // never a data write, so it's safe on Main AND custom
          // boards. The box itself is fully derived (existing v2.17.9
          // containment effect) and encloses whichever of this team's
          // members are already placed on THIS canvas.
          setTeamBoxVisible(payload.id, true);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      editable,
      reactFlowInstance,
      members,
      onTeamAssign,
      getNodes,
      handleAddToChart,
      setTeamBoxVisible,
    ]
  );
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const chartMemberIds = useMemo(
    () => new Set(nodes.filter((n) => n.type === 'person').map((n) => n.id)),
    [nodes]
  );
  const offChartMembers = members.filter((m) => !chartMemberIds.has(m.id) && m.active);

  function runAutoArrange() {
    const personIds: string[] = [];
    for (const e of edges) {
      if (e.type !== 'org') continue;
      session.noteEdgeUpdated(((e.data || {}) as OrgEdgeData).edgeId || e.id);
    }
    for (const n of nodes) if (n.type === 'person') personIds.push(n.id);
    const layouted = getLayoutedElements(nodes, edges);
    const start = performance.now();
    const startPos = new Map(nodes.map((n) => [n.id, { ...n.position }]));
    const endPos = new Map(layouted.map((n) => [n.id, { ...n.position }]));
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / 400);
      const k = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      setNodes((prev) =>
        prev.map((n) => {
          const a = startPos.get(n.id);
          const b = endPos.get(n.id);
          if (!a || !b) return n;
          return { ...n, position: { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k } };
        })
      );
      if (t < 1) requestAnimationFrame(tick);
      else session.markPositionsTouched(personIds);
    };
    requestAnimationFrame(tick);
    setEdges((prev) =>
      prev.map((e) => ({
        ...e,
        sourceHandle: undefined,
        targetHandle: undefined,
        data: { ...(e.data || {}), waypoints: [] } as OrgEdgeData,
      }))
    );
  }

  // ─── Keyboard shortcuts ───
  useEffect(() => {
    if (!editable) return;
    const NUDGE = 8;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        undo.doUndo();
        return;
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z'))
      ) {
        e.preventDefault();
        undo.doRedo();
        return;
      }
      // Esc closes any open chart menu (design law: Esc closes overlays,
      // NEVER the edit session — Cancel is a deliberate button).
      if (e.key === 'Escape') {
        setEdgeMenu(null);
        setCtxMenu(null);
        setCanvasMenu(null);
        setNoteMenu(null);
        setJunctionMenu(null);
        return;
      }
      const selectedNodes = getNodes().filter((n) => n.selected);
      const selectedEdgeIds = (edges as Edge[]).filter((ed) => ed.selected).map((ed) => ed.id);
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedEdgeIds.length > 0) {
          e.preventDefault();
          for (const id of selectedEdgeIds) deleteEdgeWithUndo(id);
        }
        const selectedNoteIds = selectedNodes
          .filter((n) => n.type === 'textBox')
          .map((n) => n.id.replace(/^note-/, ''));
        if (selectedNoteIds.length > 0) {
          e.preventDefault();
          for (const id of selectedNoteIds) deleteNoteWithUndo(id);
        }
        return;
      }
      if (
        selectedNodes.length > 0 &&
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)
      ) {
        // Person nodes only — junctions/notes use composite RF ids
        // (`junction-<id>` / `note-<id>`) that don't correspond to any
        // team_members row, so they must not flow through the
        // member-position write path below.
        const nudgeTargets = selectedNodes.filter((n) => n.type === 'person');
        if (nudgeTargets.length === 0) return;
        e.preventDefault();
        const step = e.shiftKey ? NUDGE * 5 : NUDGE;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        const updates = nudgeTargets.map((n) => ({
          id: n.id,
          orgX: n.position.x + dx,
          orgY: n.position.y + dy,
        }));
        undo.pushUndo({
          kind: 'move',
          updates: updates.map((u) => {
            const prev = members.find((mm) => mm.id === u.id);
            return {
              id: u.id,
              prevX: prev?.orgX ?? u.orgX - dx,
              prevY: prev?.orgY ?? u.orgY - dy,
              newX: u.orgX,
              newY: u.orgY,
            };
          }),
        });
        rfSetNodes((nds) =>
          nds.map((n) =>
            n.selected && n.type === 'person'
              ? { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } }
              : n
          )
        );
        applyPositionsLocal(updates);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    editable,
    undo,
    deleteEdgeWithUndo,
    deleteNoteWithUndo,
    edges,
    getNodes,
    rfSetNodes,
    applyPositionsLocal,
    members,
  ]);

  if (members.length === 0) {
    return (
      <EmptyState
        title="No team members"
        subtitle="Add team members in the Directory to get started."
      />
    );
  }

  // ─── Session lifecycle ───
  function handleEditChart() {
    sessionStartSignatureRef.current = computeChartKey();
    session.begin();
  }

  async function handleSaveSession() {
    session.setSaving(true);
    session.setError(null);
    try {
      const payload = session.buildCommitPayload(nodes, edges);
      // Canvas views (migration 041): null on Main (unchanged behavior),
      // set for a custom board — see commitOrgChart.ts's contract note.
      payload.viewId = viewSelection.activeViewId;
      // Only meaningful (and only applied server-side) on a custom
      // board — a no-op array on a Main commit.
      payload.removedMemberIds = Array.from(session.removedMemberIds);
      await commitOrgChart(payload);
      await refreshAll();
      toast('Chart saved');
      session.end();
    } catch (err: any) {
      const msg = err?.message || 'Save failed — check your connection and try again.';
      session.setError(msg);
      toast(msg, true);
    } finally {
      session.setSaving(false);
    }
  }

  function discardSession() {
    setNodes(initialNodes);
    setEdges(initialEdges);
    prevChartKey.current = computeChartKey();
    session.end();
  }

  function requestCancelSession() {
    if (session.dirtyCount > 0) {
      setChartConfirm({ kind: 'cancelSession' });
    } else {
      discardSession();
    }
  }

  // Plain function (not useCallback) — declared after the members.length
  // early return above, like its sibling session handlers, so it must
  // not be a hook (rules-of-hooks: hooks can't be called conditionally).
  function confirmRemoveFromChart(ids: string[]) {
    for (const id of ids) {
      removeNodeAndCascadeEdges(id);
      session.noteMemberRemoved(id);
      // Fire-and-forget, matching the original chart behavior — the
      // ONE immediate write in the edit-session model (its own
      // destructive confirm already gated this call).
      onRemoveFromChart(id);
    }
  }

  /** Custom-board counterpart (migration 041) — no immediate write, no
   *  confirm dialog: purely local (placement + this board's edges),
   *  lands in `removedMemberIds` at Save, fully reversible via Cancel
   *  editing in the meantime. */
  function removeFromBoard(ids: string[]) {
    for (const id of ids) {
      removeNodeAndCascadeEdges(id);
      session.noteMemberRemoved(id);
    }
  }

  /** Board-switch guard (migration 041): a dirty session must be
   *  resolved (Saved or Cancelled) before switching — an empty
   *  (0-dirty) session has nothing to lose, so it's closed silently
   *  instead of blocking. Returns whether the caller may proceed.
   *  Shared by the picker, "New view…", and "Delete view" (deleting
   *  the ACTIVE view is itself an implicit switch to Main). */
  function guardSwitch(): boolean {
    if (!session.active) return true;
    if (session.dirtyCount > 0) {
      toast('Save or cancel your changes before switching boards', true);
      return false;
    }
    session.end();
    return true;
  }

  /** "New view…" (migration 041, Josh grant 2026-08-17: "views become
   *  separate canvases/boards"): SavedViewsBar has already created the
   *  view row, switched `activeViewId` to it, and captured the current
   *  display config as its starting style — this finishes the flow by
   *  dropping straight into an edit session on what is, by
   *  construction, a BLANK board (no rows exist for a brand-new view
   *  id yet). The guarded resync effect skips `setNodes(initialNodes)`
   *  while a session is active, so nodes/edges are cleared explicitly
   *  here instead of relying on it — otherwise the canvas would still
   *  show whatever board was active a moment ago. Resetting
   *  `sessionStartSignatureRef` to '' lets the external-change-notice
   *  effect self-baseline against the NEW board on its next run instead
   *  of comparing against a stale signature computed from the OLD one
   *  (React batches this handler's state updates together with the
   *  `activeViewId` change that triggered it). */
  function handleCreateBoard() {
    sessionStartSignatureRef.current = '';
    setNodes([]);
    setEdges([]);
    session.begin();
  }

  /** "Duplicate" (People Rebuild grant, 2026-08-17 — "use as a starting
   *  point," including FROM Main): creates a new `org_chart_views` row
   *  ("<source> copy", "Only me" by default, same display config the
   *  ACTIVE board currently shows) then ONE `commitOrgChart` upserting
   *  fresh-id COPIES of the active board's rendered content —
   *  placements, edges (junction endpoints remapped to their copies,
   *  member/team endpoints kept as-is, reports-to semantics never sent
   *  — lines on a board are visual only), junctions, and notes. Reads
   *  from the live `nodes`/`edges` state rather than props: `guardSwitch`
   *  below only proceeds on a CLEAN session, so at that point rendered
   *  state == server truth for whichever board is active, Main or
   *  custom — one code path for both sources. Switches to the new board
   *  on success; on any failure, best-effort deletes the orphaned view
   *  row so nothing dangling is left behind. */
  async function handleDuplicateView() {
    if (!guardSwitch()) return;
    const sourceName = isMain ? 'Main' : viewSelection.activeView?.name || 'Board';
    const newId = crypto.randomUUID();
    const now = new Date().toISOString();
    const configSnapshot = viewSelection.currentConfig;
    const newView: OrgChartView = {
      id: newId,
      name: `${sourceName} copy`,
      config: configSnapshot as unknown as Record<string, unknown>,
      visibility: { mode: 'private' },
      createdBy: currentTeamMemberId,
      createdAt: now,
      updatedAt: now,
    };

    let viewCreated = false;
    try {
      await OrgChartViewService.save(newView);
      viewCreated = true;

      const junctionIdMap = new Map<string, string>();
      const positions: { id: string; orgX: number; orgY: number }[] = [];
      const junctionsUpsert: OrgChartJunction[] = [];
      const notesUpsert: OrgChartNote[] = [];
      for (const n of nodes) {
        if (n.type === 'person') {
          positions.push({ id: n.id, orgX: n.position.x, orgY: n.position.y });
        } else if (n.type === 'junction') {
          const oldId = n.id.replace(/^junction-/, '');
          const nid = crypto.randomUUID();
          junctionIdMap.set(oldId, nid);
          const dd = (n.data || {}) as { label?: string; color?: string };
          junctionsUpsert.push({
            id: nid,
            x: n.position.x,
            y: n.position.y,
            label: dd.label,
            color: dd.color,
            createdAt: now,
            updatedAt: now,
          });
        } else if (n.type === 'textBox') {
          const dd = (n.data || {}) as NoteNodeData;
          notesUpsert.push({
            id: crypto.randomUUID(),
            x: n.position.x,
            y: n.position.y,
            width: dd.width,
            text: dd.text,
            fontSize: dd.fontSize,
            color: dd.color,
            createdAt: now,
            updatedAt: now,
          });
        }
      }

      const edgesUpsert: OrgChartEdge[] = [];
      for (const e of edges) {
        if (e.type !== 'org' || !e.source || !e.target) continue;
        const ed = (e.data || {}) as OrgEdgeData;
        const src = parseEndpoint(e.source);
        const tgt = parseEndpoint(e.target);
        const remap = (p: { id: string; type: 'member' | 'team' | 'junction' }) =>
          p.type === 'junction' ? junctionIdMap.get(p.id) || p.id : p.id;
        edgesUpsert.push({
          id: crypto.randomUUID(),
          sourceMemberId: remap(src),
          targetMemberId: remap(tgt),
          sourceType: src.type,
          targetType: tgt.type,
          style: ed.lineStyle,
          color: ed.color,
          label: ed.label,
          sourceHandle: (e.sourceHandle as 'top' | 'bottom' | 'left' | 'right' | null) || undefined,
          targetHandle: (e.targetHandle as 'top' | 'bottom' | 'left' | 'right' | null) || undefined,
          waypoints: ed.waypoints,
          createdAt: now,
          updatedAt: now,
        });
      }

      await commitOrgChart({
        viewId: newId,
        positions,
        edges: { upsert: edgesUpsert },
        junctions: { upsert: junctionsUpsert },
        notes: { upsert: notesUpsert },
      });
      await refreshAll();
      viewSelection.markAppliedConfig(newId, configSnapshot);
      toast(`"${newView.name}" board created`);
    } catch (err: any) {
      toast(err?.message || 'Failed to duplicate view', true);
      if (viewCreated) {
        try {
          await OrgChartViewService.delete(newId);
        } catch {
          // best-effort orphan cleanup — nothing more to do if this fails too.
        }
      }
    }
  }

  const canvasContextValue = { canEdit: editable, snapEnabled, snapTargetsRef, nodeStyle };

  const viewsControls = (
    <SavedViewsBar
      views={orgChartViews}
      selection={viewSelection}
      roles={roles}
      canEdit={canEdit}
      currentTeamMemberId={currentTeamMemberId}
      isViewAdmin={isViewAdmin}
      refreshAll={refreshAll}
      toast={toast}
      guardSwitch={guardSwitch}
      onViewCreated={handleCreateBoard}
      onDuplicateView={handleDuplicateView}
    />
  );

  const viewModePill = (
    <span
      title={canEdit ? undefined : "You don't have permission to edit team members"}
      style={{
        fontSize: 'var(--font-size-xs)',
        color: 'var(--tm)',
        padding: rem(2) + ' var(--space-2)',
        border: '1px solid var(--bd)',
        borderRadius: 'var(--radius-full)',
        background: 'var(--sf)',
      }}
    >
      View mode &middot; scroll to zoom &middot; hover a node for details &middot; double-click for
      full profile
    </span>
  );

  const sessionControls = !canEdit ? (
    viewModePill
  ) : !session.active ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      {viewModePill}
      <Button size="sm" onClick={handleEditChart} title="Open the chart for editing">
        Edit chart
      </Button>
    </div>
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', gap: rem(10) }}>
      {session.externalChangeNotice && (
        <span
          style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-warning)' }}
          title="Someone else changed the chart while you were editing. Saving now will overwrite their change with yours."
        >
          Chart changed elsewhere &middot; saving will overwrite
        </span>
      )}
      {session.error && (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--dg)' }}>{session.error}</span>
      )}
      <span
        style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)' }}
        title="Lines are straight by default. Right-click a line to add a turn (drag turns to shape it, right-click a turn to delete it) or a junction, change its style and color, or add a label. Reset shape clears all turns and junctions. Right-click a junction to delete it."
      >
        Lines are straight by default &middot; right-click a line to add a turn or junction, drag
        turns to shape it &middot; drag a side dot to connect &middot; right-click a junction to
        delete it &middot; Ctrl+Z undo
      </span>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)' }}>
        {session.dirtyCount} unsaved change{session.dirtyCount !== 1 ? 's' : ''}
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={requestCancelSession}
        disabled={session.saving}
        title="Discard everything since you opened the chart for editing"
      >
        Cancel editing
      </Button>
      <Button
        size="sm"
        onClick={handleSaveSession}
        disabled={session.saving}
        title="Save every change from this editing session"
      >
        {session.saving ? 'Saving…' : 'Save chart'}
      </Button>
    </div>
  );

  return (
    <OrgChartCanvasProvider value={canvasContextValue}>
      {/* data-chart-editing: orgchart.css keys always-visible anchor dots
          off this while an edit session is open (Josh 2026-08-18). */}
      <div
        data-chart-editing={editable ? '' : undefined}
        style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%' }}
      >
        {editable && (
          <PaletteBar
            search={search}
            onSearchChange={setSearch}
            offChartMembers={offChartMembers}
            teams={teams}
            getMemberTeamColor={getMemberTeamColor}
          />
        )}

        <OrgChartToolbar
          editable={editable}
          onAutoArrange={() => setChartConfirm({ kind: 'autoArrange' })}
          snapEnabled={snapEnabled}
          setSnapEnabled={setSnapEnabled}
          teams={teams}
          visibleTeamIds={visibleTeamIds}
          setTeamBoxVisible={setTeamBoxVisible}
          setAllTeamsVisible={setAllTeamsVisible}
          nodeStyle={nodeStyle}
          setNodeStyle={setNodeStyle}
          legendVisible={legendVisible}
          setLegendVisible={setLegendVisible}
          printNodes={nodes}
          printEdges={edges}
          viewsControls={viewsControls}
          sessionControls={sessionControls}
        />

        <div
          style={{
            flex: 1,
            borderRadius: 10,
            border: '1px solid var(--bd)',
            overflow: 'hidden',
            position: 'relative',
          }}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onMouseMove={
            reactFlowInstance
              ? onCanvasMouseMove(reactFlowInstance.screenToFlowPosition)
              : undefined
          }
          onMouseLeave={onCanvasMouseLeave}
        >
          {/* Persistent legend overlay (Josh grant 2026-08-19) — view mode
              only, hides during an edit session for canvas working room;
              toolbar's Legend toggle controls `legendVisible`. */}
          {!editable && legendVisible && <LegendOverlay nodes={nodes} />}
          {!isMain && editable && chartMemberIds.size === 0 && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                zIndex: 1,
                textAlign: 'center',
                maxWidth: 280,
                fontSize: 'var(--font-size-sm)',
                color: 'var(--tm)',
              }}
            >
              Drag people from the tray to start this board
            </div>
          )}
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeDragStart={editable ? wrappedOnNodeDragStart : undefined}
            onNodeDrag={editable ? wrappedOnNodeDrag : undefined}
            onNodeDragStop={editable ? wrappedOnNodeDragStop : undefined}
            onConnect={editable ? onConnect : undefined}
            onReconnect={editable ? onReconnect : undefined}
            isValidConnection={editable ? isValidConnection : () => false}
            connectionMode={ConnectionMode.Loose}
            nodesDraggable={editable}
            nodesConnectable={editable}
            edgesReconnectable={editable}
            onNodeContextMenu={editable ? onNodeContextMenu : undefined}
            onEdgeContextMenu={editable ? onEdgeContextMenu : undefined}
            onPaneContextMenu={(event) => {
              event.preventDefault();
              const me = event as React.MouseEvent;
              setCanvasMenu({ x: me.clientX, y: me.clientY });
            }}
            onNodeDoubleClick={onNodeDoubleClick}
            onEdgeDoubleClick={editable ? onEdgeDoubleClick : undefined}
            onPaneClick={() => {
              setCtxMenu(null);
              setEdgeMenu(null);
              setCanvasMenu(null);
              setJunctionMenu(null);
            }}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onInit={(instance) => {
              setReactFlowInstance(instance);
              setRfInitialized(true);
            }}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            panOnDrag
            /* Explorer semantics on a node canvas (Phase 1 item 5,
               docs/MASTERS_UNIFIED_PLAN.md): Ctrl/Cmd-click toggles one card
               in or out of the selection, and Shift-drag boxes a group. A
               chart has no list order for a Shift RANGE to run along, so the
               box IS the range gesture here — the same trade Figma makes. */
            selectionKeyCode="Shift"
            multiSelectionKeyCode={['Control', 'Meta']}
            minZoom={0.05}
            maxZoom={3}
            defaultEdgeOptions={{ type: 'org', style: { stroke: 'var(--tx)', strokeWidth: 1.5 } }}
            style={{ background: 'var(--sf)' }}
            proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="var(--bd)" />
            <Controls
              showInteractive={false}
              style={{ borderRadius: 8, border: '1px solid var(--bd)', boxShadow: 'none' }}
            />
            <MiniMap
              nodeColor={(n: Node) => {
                const dd = n.data as PersonNodeData;
                return dd?.teamColor || 'var(--ac)';
              }}
              maskColor="rgba(0,0,0,0.08)"
              style={{ borderRadius: 8, border: '1px solid var(--bd)' }}
            />
            <SnapGuideLines hY={dragAndSnap.snapLines.hY} vX={dragAndSnap.snapLines.vX} />
            <RemoteCursors cursors={remoteCursors} />
          </ReactFlow>
        </div>

        {edgeMenu &&
          (() => {
            const rf = edges.find(
              (ed) => (((ed.data || {}) as OrgEdgeData).edgeId || ed.id) === edgeMenu.edgeId
            );
            const edge = rf ? edgeFromRF(rf, edgeMenu.edgeId) : null;
            if (!edge || !rf) return null;
            const geom = edgeGeoms.find((g) => g.edgeId === edge.id);
            const s = resolveHandlePoint(rf.source, rf.sourceHandle);
            const t = resolveHandlePoint(rf.target, rf.targetHandle);
            const polyline = geom?.polyline || (s && t ? [s, t] : null);
            const clicked = reactFlowInstance
              ? reactFlowInstance.screenToFlowPosition({ x: edgeMenu.x, y: edgeMenu.y })
              : null;
            const existingWaypoints = edge.waypoints || [];
            // Right-click landed within 12px of an existing turn
            // (People Rebuild 2026-08-18, "Delete turn... when the
            // click is within 12px of a turn").
            const TURN_HIT_PX = 12;
            let nearTurnIndex: number | null = null;
            if (clicked) {
              for (let i = 0; i < existingWaypoints.length; i++) {
                const wp = existingWaypoints[i];
                if (Math.hypot(wp.x - clicked.x, wp.y - clicked.y) <= TURN_HIT_PX) {
                  nearTurnIndex = i;
                  break;
                }
              }
            }
            return (
              <EdgeMenu
                edge={edge}
                x={edgeMenu.x}
                y={edgeMenu.y}
                onUpdateEdge={(patch) => updateEdgeWithUndo(edge.id, patch)}
                onDeleteEdge={() => {
                  setEdgeMenu(null);
                  deleteEdgeWithUndo(edge.id);
                }}
                onEditLabel={() => {
                  setLabelEditor({
                    edgeId: edge.id,
                    value: edge.label || '',
                    x: edgeMenu.x,
                    y: edgeMenu.y,
                  });
                  setEdgeMenu(null);
                }}
                onAddTurnHere={() => {
                  setEdgeMenu(null);
                  if (!clicked || !polyline) return;
                  const { point, segIndex } = projectPointOntoPolylineIndexed(polyline, clicked);
                  const nextWaypoints = [
                    ...existingWaypoints.slice(0, segIndex),
                    point,
                    ...existingWaypoints.slice(segIndex),
                  ];
                  updateEdgeWithUndo(edge.id, { waypoints: nextWaypoints });
                }}
                nearTurn={nearTurnIndex !== null}
                onDeleteTurnHere={() => {
                  setEdgeMenu(null);
                  if (nearTurnIndex === null) return;
                  const nextWaypoints = existingWaypoints.filter((_, i) => i !== nearTurnIndex);
                  updateEdgeWithUndo(edge.id, { waypoints: nextWaypoints });
                }}
                onAddJunctionHere={() => {
                  setEdgeMenu(null);
                  if (!clicked) return;
                  const target = polyline ? projectPointOntoPolyline(polyline, clicked) : clicked;
                  handleSpawnJunction(edge.id, target.x, target.y);
                }}
              />
            );
          })()}

        {labelEditor &&
          (() => {
            const cancel = () => setLabelEditor(null);
            const commit = async () => {
              const val = labelEditor.value.trim();
              await updateEdgeWithUndo(labelEditor.edgeId, { label: val || undefined });
              setLabelEditor(null);
            };
            return (
              <div
                data-orgchart-menu
                style={{
                  position: 'fixed',
                  left: labelEditor.x,
                  top: labelEditor.y,
                  zIndex: 1000,
                  background: 'var(--sa)',
                  border: '1px solid var(--ac)',
                  borderRadius: 8,
                  padding: 6,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                }}
              >
                <input
                  autoFocus
                  aria-label="Line label"
                  value={labelEditor.value}
                  placeholder="Line label…"
                  onChange={(e) => setLabelEditor({ ...labelEditor, value: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commit();
                    if (e.key === 'Escape') cancel();
                  }}
                  onBlur={commit}
                  style={{
                    width: 180,
                    padding: '6px 8px',
                    borderRadius: 6,
                    border: '1px solid var(--bd)',
                    background: 'var(--sf)',
                    color: 'var(--tx)',
                    fontSize: 13,
                    outline: 'none',
                  }}
                />
              </div>
            );
          })()}

        {ctxMenu &&
          (() => {
            const m = members.find((x) => x.id === ctxMenu.memberId);
            if (!m) return null;
            const selectedNodes = getNodes().filter((n) => n.selected);
            const selectedIds =
              selectedNodes.length > 1 && selectedNodes.some((n) => n.id === m.id)
                ? selectedNodes.map((n) => n.id)
                : [m.id];
            return (
              <NodeContextMenu
                member={m}
                selectedIds={selectedIds}
                members={members}
                teams={teams}
                teamAssignments={teamAssignments}
                x={ctxMenu.x}
                y={ctxMenu.y}
                isMain={isMain}
                onDetach={(ids) => {
                  // Main-only — see sessionCreateEdge's mirror-block
                  // note; also hidden on custom boards in the menu
                  // itself, this is defense in depth.
                  if (!isMain) return;
                  for (const id of ids) session.setReportsTo(id, '__root__');
                  const removed = edges.filter((e) => ids.includes(e.target || ''));
                  setEdges((prev) => prev.filter((e) => !ids.includes(e.target || '')));
                  for (const e of removed)
                    session.noteEdgeDeleted(((e.data || {}) as OrgEdgeData).edgeId || e.id);
                }}
                onToggleTeamRole={onToggleTeamRole}
                onTeamRemove={onTeamRemove}
                onAlign={dragAndSnap.alignSelection}
                onDistribute={dragAndSnap.distributeSelection}
                onRequestRemoveFromChart={(ids, names) =>
                  setChartConfirm({ kind: 'removeFromChart', ids, names })
                }
                onRemoveFromView={removeFromBoard}
                onEditMember={onMemberClick}
                onDeleteMembers={(ids) => {
                  for (const id of ids) onDeleteMember(id);
                }}
                onClose={() => setCtxMenu(null)}
              />
            );
          })()}

        {canvasMenu && (
          <CanvasContextMenu
            x={canvasMenu.x}
            y={canvasMenu.y}
            canEdit={editable}
            snapEnabled={snapEnabled}
            onFitView={() => reactFlowInstance?.fitView({ padding: 0.2, duration: 200 })}
            onAutoArrange={() => setChartConfirm({ kind: 'autoArrange' })}
            onToggleSnap={() => setSnapEnabled(!snapEnabled)}
            onShowAllTeamBoxes={() => setAllTeamsVisible(true)}
            onHideAllTeamBoxes={() => setAllTeamsVisible(false)}
            onAddTextBox={() => {
              handleAddTextBox(canvasMenu.x, canvasMenu.y);
              setCanvasMenu(null);
            }}
            onAddJunction={() => {
              handleAddJunction(canvasMenu.x, canvasMenu.y);
              setCanvasMenu(null);
            }}
            onClose={() => setCanvasMenu(null)}
          />
        )}

        {noteMenu &&
          (() => {
            const rf = getNodes().find((n) => n.id === `note-${noteMenu.noteId}`);
            const note = rf ? noteFromRF(rf, noteMenu.noteId) : null;
            if (!note) return null;
            return (
              <TextBoxMenu
                note={note}
                x={noteMenu.x}
                y={noteMenu.y}
                onUpdateNote={(patch) => {
                  const prev: NotePatchFields = {};
                  const noteRec = note as unknown as Record<string, unknown>;
                  for (const k of Object.keys(patch) as (keyof OrgChartNote)[]) {
                    (prev as Record<string, unknown>)[k] = noteRec[k];
                  }
                  undo.pushUndo({ kind: 'note-patch', noteId: note.id, prev, next: patch });
                  sessionUpdateNote(note.id, patch);
                }}
                onDeleteNote={() => {
                  setNoteMenu(null);
                  deleteNoteWithUndo(note.id);
                }}
              />
            );
          })()}

        {junctionMenu && (
          <JunctionMenu
            x={junctionMenu.x}
            y={junctionMenu.y}
            onDeleteJunction={() => deleteJunctionWithUndo(junctionMenu.junctionId)}
            onClose={() => setJunctionMenu(null)}
          />
        )}

        <ConfirmDialog
          open={chartConfirm !== null}
          onOpenChange={(o) => !o && setChartConfirm(null)}
          title={
            chartConfirm?.kind === 'autoArrange'
              ? 'Auto-arrange the chart?'
              : chartConfirm?.kind === 'cancelSession'
                ? 'Discard unsaved chart changes?'
                : 'Remove from chart?'
          }
          description={
            chartConfirm?.kind === 'autoArrange'
              ? 'Auto-arrange will reset all node positions and clear any custom line routing. Continue?'
              : chartConfirm?.kind === 'cancelSession'
                ? `You have ${session.dirtyCount} unsaved change${session.dirtyCount !== 1 ? 's' : ''}. Cancelling now restores the chart to how it looked before you started editing.`
                : `Remove ${chartConfirm && chartConfirm.kind === 'removeFromChart' && chartConfirm.names ? chartConfirm.names : 'the selected member(s)'} from the chart? This clears their reporting line AND removes every team assignment — it is not just a visual change.`
          }
          confirmLabel={
            chartConfirm?.kind === 'autoArrange'
              ? 'Auto-arrange'
              : chartConfirm?.kind === 'cancelSession'
                ? 'Discard changes'
                : 'Remove'
          }
          destructive={chartConfirm?.kind !== 'autoArrange'}
          onConfirm={() => {
            if (chartConfirm?.kind === 'autoArrange') {
              runAutoArrange();
            } else if (chartConfirm?.kind === 'removeFromChart') {
              confirmRemoveFromChart(chartConfirm.ids);
            } else if (chartConfirm?.kind === 'cancelSession') {
              discardSession();
            }
            setChartConfirm(null);
          }}
        />
      </div>
    </OrgChartCanvasProvider>
  );
}
